import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import * as _ from 'lodash-es';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppService } from '../../app.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { GridEventService } from './services/grid-event.service';
import { ModalFactoryService } from '../../global/services/modal-factory.service';
import { environment } from '../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { GlobalService } from '../../global/services/global.service';
import { GridSaveFactoryService } from './services/grid-save-factory.service';
import { GridFactory } from './services/grid.service';
import { GRID_SCAFFOLD_HTML } from './grid-scaffold';
import { ComponentRegistryService } from './services/component-registry.service';
import { TabGridLoaderService, TabGridResult } from './services/tab-grid-loader.service';
import {
  CeHostDialogComponent,
  CeHostDialogData,
  ModalLockPayload,
} from '../../shared/components/ce-host-dialog/ce-host-dialog.component';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { SimpleConfirmDialogComponent } from '../../shared/components/simple-confirm-dialog/simple-confirm-dialog.component';

declare var $: any;

/**
 * API URL + default param fallback for legacy AngularJS-only screens whose grid
 * endpoint is declared in `$stateProvider` (in the legacy `efile.js` / similar
 * bundles) but NOT returned by `/getRoutes` (the backend returns `{"get":""}`
 * for them, so `appService.ROUTES[link].api.get` is empty).
 *
 * `url` mirrors the exact URL from the AngularJS state definition. `params`
 * mirrors the default request body that the legacy controller was sending on
 * first load (e.g. `SubmissionListCtrl` hardcodes `type: 'federal'`) — without
 * these the backend returns an empty grid.
 *
 * Add new screens here as they surface during migration. Long-term these
 * should be added server-side to `/getRoutes` and this map can shrink.
 */
interface LegacyRouteApi {
  url: string;
  params?: Record<string, any>;
}
const LEGACY_ROUTE_API_GET: Record<string, LegacyRouteApi> = {
  // Keep the URL in the same shape `/getRoutes` returns for server-registered
  // screens (relative, no leading slash). `loadGridData` below prepends
  // `environment.base_url` (`/gtw`) when the URL is not absolute.
  'submissions-dasboard.list': {
    url: 'loadGridJson?grid_id=5746&action_id=32096',
    // `SubmissionListCtrl` defaults to federal submissions on first load.
    params: { type: 'federal' },
  },
};

const PDF_ISSUE_SCENARIO_GRID_LOADS = [
  { screenKey: '10710', gridId: '5441', actionId: '31379' }, // PDF DD
  { screenKey: '10711', gridId: '5442', actionId: '31380' }, // PDF formbucket
];

/** Column metadata for Source modal after Add Tag → Save and Source (legacy AttributeCtrl.showSoucePage). */
const NEW_TAG_SOURCE_COL_DATA: Record<string, any> = {
  grid_header_key: '568755',
  grid_key: '204',
  label: 'Source',
  full_label: null,
  map: 'EDIT',
  width: 80,
  data_type: 'string',
  format: null,
  edit_type: 'form',
  validation: null,
  editable: 'y',
  display: 'y',
  call_back: 'newTagSummaryFTC',
  header_class: null,
  col_class: '',
  drilldown: null,
  priority: 2,
  new_row_editable: '0',
  static_values: null,
  required: '0',
  data_api: null,
  apply_rules: null,
  edit_rules: null,
  edit_rule_message: null,
  edit_icon: 'edit',
  object_id: '1',
};

@Directive({
  selector: '[gg-grid]',
  standalone: false,
})
export class GridDirective implements OnInit, AfterViewInit, OnDestroy {
  @Output() ggCallBack = new EventEmitter<any>();
  ggGrid: any;
  ggApi: any;
  private tabSub!: Subscription;
  private refreshGridSub?: Subscription;
  /**
   * Subscription to the per-tab result stream owned by `TabGridLoaderService`.
   * UI-only — the underlying HTTP subscription lives in the service and
   * survives directive teardown so requests keep running across tab switches.
   */
  private uiSub?: Subscription;
  private refreshSub?: Subscription;
  private downloadExcelSub?: Subscription;
  private gridInitialized = false;
  private currentScreenKey: string | null = null;
  private gridLayoutRefreshToken = 0;
  private alertObserver: MutationObserver | null = null;

  /** Workspace import tabs; must stay in sync with `DataFilters` / `grid-import-toolbar`. */
  private static readonly IMPORT_GRID_SCREEN_TYPES: readonly string[] = [
    'grid-import',
    'grid-import-td',
    'grid-import-pivot',
    'grid-import-validation',
  ];

  constructor(
    private element: ElementRef,
    private appService: AppService,
    private workspaceService: WorkspaceService,
    public gridEventService: GridEventService,
    private modalFactory: ModalFactoryService,
    public viewContainer: ViewContainerRef,
    public dialog: MatDialog,
    private globalService: GlobalService,
    private gridSaveFactory: GridSaveFactoryService,
    public gridFactory: GridFactory,
    private componentRegistry: ComponentRegistryService,
    private http: HttpClient,
    private gtwSnackbarService: GtwSnackbarService,
    private tabGridLoader: TabGridLoaderService
  ) {}

  private commentsSub?: Subscription;

  ngOnInit(): void {
    this.refreshSub = this.gridSaveFactory.gridRefreshNeeded$.subscribe(
      (_callName: string) => {
        const activeScreen = this.workspaceService.workspaceFactory?.activeScreen;
        if (activeScreen?.screen_key && this.ggApi) {
          this.loadGridData(activeScreen, true);
        } else if (this.ggApi?.gridRefresh) {
          this.ggApi.gridRefresh();
        }
      }
    );

    this.commentsSub = this.gridSaveFactory.commentsReloadNeeded$.subscribe(
      (type: string) => {
        this.loadMessages(type || 'all');
      }
    );

    this.downloadExcelSub = this.workspaceService.downloadExcel.subscribe(() => {
      this.exportToExcel();
    });
  }

  ngAfterViewInit(): void {
    if (typeof $ === 'undefined') {
      console.warn('[GridDirective] jQuery not loaded, skipping grid init');
      return;
    }

    this.ggGrid = $(this.element.nativeElement);

    this.tabSub = this.workspaceService.setTabActive.subscribe(
      (workspace: any) => {
        if (workspace?.screen_key) {
          // Same-screen re-emit while filters are mid-reload: keep the
          // current grid in place. Without this guard, a spurious
          // `setTabActive.next(activeScreen)` (typically fired after
          // `globalParamsChanged$` nulled `activeScreen.filters` and
          // `dataFiltersService.loadFilters` is still in-flight) would
          // run `destroyGrid()` and then bail in `initGrid()` on
          // "filters not loaded" — tearing down a fully-rendered grid
          // for no reason. The natural re-init paths (Filter button,
          // drill-down comment-link cascade, or a real tab switch) will
          // re-enter here with `filters.loaded === true` and re-init
          // normally. Symptom this protects against: admin entity
          // screens (10898/10895/11149/…) where the new screen's
          // `jcd_key` differs from the previous, causing the next
          // `globalParamsChanged$` emission to wipe the freshly-loaded
          // FilterObj and bail the grid.
          const activeScreen = this.workspaceService?.workspaceFactory?.activeScreen;
          const isImportScreen = this.isImportScreenForFetch(activeScreen);
          const filtersReady = !!activeScreen?.filters?.loaded || isImportScreen;
          const sameScreen = String(this.currentScreenKey ?? '')
            === String(workspace.screen_key ?? '');
          if (sameScreen && this.gridInitialized && !filtersReady) {
            console.log(
              '[GridDirective] tabSub deferred: filters mid-reload for same screen, keeping current grid'
            );
            this.workspaceService.gridLoading.next(false);
            return;
          }
          // Capture the incoming tab's screen_key so setupCallbacks /
          // initGrid broadcast and subscribe on the same key, regardless of
          // the global workspaceFactory.activeScreen state.
          this.currentScreenKey = workspace.screen_key;
          this.workspaceService.gridLoading.next(true);
          this.destroyGrid();
          this.setupCallbacks();
          this.setupGridEventListeners();
          this.initGrid();
        }
      }
    );

    this.refreshGridSub = this.workspaceService.refreshGridData.subscribe(
      (activeScreen: any) => {
        if (activeScreen?.screen_key && this.ggApi) {
          this.loadGridData(activeScreen, true);
        }
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Callback routing — gizmoGrid fires options.callBack({callName, data, type})
  // ---------------------------------------------------------------------------

  private setupCallbacks(): void {
    const screenKey = this.currentScreenKey;
    if (!screenKey) return;

    const callBack = (object: any) => {
      const _data = object.data;
      const _type = object.type;
      const _callName = object.callName;
      this.ggCallBack.emit(object);

      switch (_type) {
        case 'link':
          this.handleLink(_data);
          break;
        case 'form':
          this.handleFormCallback(object);
          break;
        case 'modal':
          this.openModal(_data);
          break;
        case 'inline-save':
          this.inlineSave(_data);
          break;
        case 'input':
          this.handleInputSave(_callName, _data);
          break;
        case 'batch-save':
          this.handleBatchSave(_callName, _data);
          break;
        case 'columnTemplate':
          this.handleColumnTemplate(_callName, _data);
          break;
        case 'openGridModal':
          this.handleOpenGridModal(_callName, _data);
          break;
        case 'row-added':
          this.handleRowAdded(_callName, _data);
          break;
        case 'reloadComment':
          this.handleReloadComment(_data);
          break;
        case 'comment':
          this.handleModalByFactory(_callName, _data);
          break;
        case 'attachment':
          this.handleModalByFactory(_callName, _data);
          break;
        case 'preview':
          this.handleModalByFactory(_callName, _data);
          break;
        case 'reloadAttachment':
          this.handleReloadAttachment();
          break;
        case 'batchSave':
          this.handleBatchSaveAction(_callName, _data);
          break;
        case 'drillDown':
          this.handleDrillDown(_callName, _data);
          break;
        case 'auditcell':
          this.handleModalByFactory(_callName, _data);
          break;
        case 'downloadDoc':
          this.handleDownloadDoc(_callName, _data);
          break;
        case 'removeBatchSelect':
          this.handleRemoveBatchSelect(_data);
          break;
        case 'applyPivotTemplate':
          this.handleApplyPivotTemplate(_callName, _data);
          break;
        default:
          break;
      }
    };

    this.gridEventService.on(screenKey, 'grid', callBack);
  }

  // ---------------------------------------------------------------------------
  // Grid event listeners — replaces AngularJS $scope.$on → ggApi calls
  // ---------------------------------------------------------------------------

  private setupGridEventListeners(): void {
    const activeScreen = this.workspaceService?.workspaceFactory?.activeScreen;
    if (!activeScreen?.screen_key) return;

    this.gridEventService.on('gridRefresh', 'directive', () => {
      if (this.isGridInlineEditActive()) {
        return;
      }
      this.ggApi?.gridRefresh?.();
    });

    this.gridEventService.on('dataUpdate', 'directive', (data: any) => {
      this.ggApi?.updateData?.(data);
    });

    this.gridEventService.on('gridRefreshPipe', 'directive', (data: any) => {
      this.ggApi?.gridRefreshPipe?.(data);
    });

    this.gridEventService.on('showDataRefresh', 'directive', (show: any) => {
      this.ggApi?.dataRefresh?.(show);
    });

    this.gridEventService.on('deleteRows', 'directive', (array: any) => {
      this.ggApi?.deleteRows?.(array);
    });

    this.gridEventService.on('deleteRow', 'directive', (objectId: any) => {
      this.ggApi?.deleteRow?.(objectId);
    });

    this.gridEventService.on('gridDeleteSelectedRow', 'directive', (objectId: any) => {
      this.ggApi?.deleteSelectedRow?.(objectId);
    });

    this.gridEventService.on('showLoader', 'directive', (show: any) => {
      if (_.isObject(show) && (show as any).isLoader) {
        this.ggApi?.showLoader?.((show as any).isFromModal);
      } else if (_.isObject(show) && !(show as any).isLoader) {
        this.ggApi?.hideLoader?.();
      } else if (!_.isObject(show) && show) {
        this.ggApi?.showLoader?.();
      } else if (!_.isObject(show) && !show) {
        this.ggApi?.hideLoader?.();
      }
    });

    this.gridEventService.on('hideLoader', 'directive', () => {
      this.ggApi?.hideLoader?.();
    });

    this.gridEventService.on('removeNewRow', 'directive', () => {
      this.ggApi?.removeNewRow?.();
    });

    this.gridEventService.on('loadActonTemplates', 'directive', (data: any) => {
      this.ggApi?.loadActonTemplates?.(data);
      const activeScreen = this.workspaceService.workspaceFactory?.activeScreen;
      if (activeScreen?.data && Array.isArray(data)) {
        activeScreen.data.actionTemplates = data;
      }
    });

    this.gridEventService.on('navPanel', 'directive', (data: any) => {
      this.ggApi?.adjustModal?.(data);
    });

    this.gridEventService.on('loadGridMessages', 'directive', (type: any) => {
      this.loadMessages(type);
    });

    this.gridEventService.on('loadGridAttachments', 'directive', () => {
      this.loadAttachments();
    });

    this.gridEventService.on('releaseData', 'directive', () => {
      this.ggApi?.releaseData?.();
    });

    this.gridEventService.on('dataLoaded', 'directive', (data: any) => {
      const activeScreen = this.workspaceService?.workspaceFactory?.activeScreen;
      if (activeScreen && data && this.ggApi) {
        this.processAndLoadData(activeScreen, data);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Grid initialization and data loading
  // ---------------------------------------------------------------------------

  private initGrid(): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    if (!activeScreen?.screen_key || !this.ggGrid) {
      console.log('[GridDirective] initGrid bail: no screen_key or ggGrid');
      this.workspaceService.gridLoading.next(false);
      return;
    }
    if (typeof $ === 'undefined' || typeof $.fn.gizmoGrid !== 'function') {
      console.log('[GridDirective] initGrid bail: no jQuery/gizmoGrid');
      this.workspaceService.gridLoading.next(false);
      return;
    }

    const isImportScreen = this.isImportScreenForFetch(activeScreen);
    if (!activeScreen.filters?.loaded && !isImportScreen) {
      console.log('[GridDirective] initGrid bail: filters not loaded');
      this.workspaceService.gridLoading.next(false);
      return;
    }

    this.ggGrid.html(GRID_SCAFFOLD_HTML);

    const screenKey = this.currentScreenKey ?? activeScreen.screen_key;
    const callBack = (obj: any) => {
      this.ggCallBack.emit(obj);
      this.gridEventService.broadcast(screenKey, obj);
    };

    this.ggGrid.gizmoGrid({
      fixedColumn: 2,
      gridHeight: 'auto',
      defaultRowHeight: 35,
      callBack: callBack,
    });
    this.ggApi = this.ggGrid.data('gizmoGridApi');
    this.gridInitialized = true;

    this.setupAlertObserver();

    this.ggGrid.on('mousewheel.ggMultiSelect DOMMouseScroll.ggMultiSelect', '.gg-multi-select', (event: any) => {
      event.stopPropagation();
    });

    this.ggGrid.on('click.filteredExportSync', '.tools-export', () => {
      this.syncFilteredExportVisibility();
    });

    this.ggGrid.on('click.exportAction', '.gg-export-action', (event: any) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      const action = $(event.currentTarget).attr('action');
      if (action === 'filtered-export') {
        this.exportToExcelFiltered();
      } else {
        this.exportToExcel();
      }
    });

    this.syncFilteredExportVisibility();

    // Background fetch already running for this tab (user navigated away
    // and came back) or finished cached payload sitting in the loader
    // service: re-enter loadGridData so the overlay shows again and the
    // pending result is wired into this freshly-recreated directive
    // instance. Without this guard `DataFilters` emits dataFetched=false
    // on tab return (no `screen.data` yet) and we'd bail to the empty
    // "Please use filters above" placeholder despite the request being
    // mid-flight.
    const tabKey = activeScreen.screen_key;
    const hasInFlight = !!tabKey && this.tabGridLoader.isLoading(tabKey);
    const hasCache = !!tabKey && !!this.tabGridLoader.getCached(tabKey);

    if (this.workspaceService.dataFetched.value || hasInFlight || hasCache) {
      console.log(
        `[GridDirective] initGrid → loadGridData (dataFetched=${this.workspaceService.dataFetched.value}, inFlight=${hasInFlight}, cache=${hasCache})`
      );
      this.loadGridData(activeScreen);
    } else {
      console.log('[GridDirective] initGrid bail: dataFetched=false, no inflight, no cache');
      this.workspaceService.gridLoading.next(false);
    }
  }

  private handleGridLoadError(context: string, err: any, screen?: any): void {
    console.error(`[GridDirective] ${context}:`, err);
    this.ggApi?.hideLoader?.();
    this.ggApi?.hideOverLay?.();
    this.workspaceService.gridLoading.next(false);
    this.workspaceService.dataFetched.next(false);
    // Snackbar for errors on the *active* tab is owned by this directive so
    // the message can show alongside the cleared loader. Background-tab
    // errors are surfaced by `WorkspaceComponent`'s subscription on
    // `TabGridLoaderService.errors$` to avoid double toasts here.
    const label = screen?.label;
    const message = label
      ? `Failed to load data for "${label}". Please try again.`
      : 'Failed to load grid data. Please try again.';
    this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--red');
  }

  private isGridFetchSuccess(data: any): boolean {
    const cs = data?.callSuccess;
    return cs === '1' || cs === 1 || String(cs) === '1';
  }

  private loadGridData(activeScreen: any, forceRefresh = false): void {
    if (!this.ggApi) {
      // The loader flag was flipped on in `setTabActive` before `initGrid` ran; if we
      // bail out before issuing a request, we must clear it or the overlay stays
      // visible forever on the active tab.
      this.workspaceService.gridLoading.next(false);
      return;
    }

    // Capture the user's column filter chips / sort / global-search BEFORE
    // the loader pipeline mutates `activeScreen.data`. This MUST happen
    // before the `clearCache(activeScreen)` call below (which nulls
    // `screen.data`) and before `TabGridLoaderService.finalize()` later
    // reassigns `screen.data` to the freshly fetched payload — by the time
    // `processAndLoadData` runs, `activeScreen.data` already points at the
    // new server response and the previous tableState is irrecoverably
    // gone from there. The captured snapshot lives on a transient
    // `_preservedTableStateOnReload` property that `processAndLoadData`
    // consumes (and deletes) when it re-applies the chips on the new
    // payload. Restores legacy `$broadcast("gridRefresh")` parity for
    // every `loadGridData(_, true)` caller (refreshSub, refreshGridSub,
    // CeHostDialog action='refresh', batch action refresh, …).
    if (forceRefresh) {
      this.captureTableStateForReload(activeScreen);
    }

    // If a new drill-down/link navigation just landed, cached data is for
    // the previous row's filter value and must be discarded. Otherwise
    // switching between Summary rows re-renders the first row's result
    // because the cache shortcut below skips the HTTP call that would have
    // picked up the new `combination_key` / LEID.
    //
    // The presence of `commentLink` itself is the signal that this is a
    // fresh, not-yet-processed navigation: `processAndLoadData` nulls
    // `commentLink` after handling it, so any non-null value here must
    // have come from the most recent `loadLink` call.
    //
    // Default behavior (preserved for the rest of the app): we only force
    // a refresh when `commentLink.filters` is non-empty, i.e. the source
    // shipped explicit row-level predicates. Drill-downs whose target
    // inherits its FilterObj from the source via shared `filter_key` ship
    // `commentLink.filters = []` and historically reused the cache.
    //
    // Targeted exception — only the screens that demonstrated the
    // "stale cached row after re-filtering Summary" bug get the wider
    // bypass (cache skipped on every drill-down/link entry, regardless
    // of `filters.length`). Kept as an explicit allow-list to avoid
    // touching the ~318 other `filter_inherit=1` screens whose drilldown
    // behavior we have not validated.
    //   - workspace.gaap-amount-drilldown      → HO/Branch GAAP Amount Drilldown (screen 5005)
    //   - workspace.brus-gaap-amount           → FGN BRUS GAAP Amount Drilldown
    //   - workspace.8858-gaap-amount-drilldown → 8858 GAAP Amount Drilldown (screen 10165)
    //     same CFC/PTSP E&P Consolidation activity as 5005, same drill-down
    //     pattern, same stale-cache symptom on subsequent drill-downs.
    const NARROW_DRILLDOWN_CACHE_BYPASS_LINKS = new Set<string>([
      'workspace.gaap-amount-drilldown',
      'workspace.brus-gaap-amount',
      'workspace.8858-gaap-amount-drilldown',
    ]);
    const pendingCommentLink = activeScreen.commentLink;
    const isPendingDrillNav = !!pendingCommentLink
      && (pendingCommentLink.type === 'drillDown' || pendingCommentLink.type === 'link');
    const hasNonEmptyCommentFilters = isPendingDrillNav
      && Array.isArray(pendingCommentLink.filters)
      && pendingCommentLink.filters.length > 0;
    const isNarrowFixTarget = isPendingDrillNav
      && NARROW_DRILLDOWN_CACHE_BYPASS_LINKS.has(String(activeScreen.link || ''));
    const hasPendingDrillDown = hasNonEmptyCommentFilters || isNarrowFixTarget;

    // Cache hit fast path — pulled from the singleton loader so a tab that
    // received its data while inactive (cross-tab background fetch) renders
    // instantly on revisit, with no new HTTP call.
    //
    // `firstLoad` semantics:
    //   - 0 → "user is re-visiting a tab they already rendered" → preserve
    //     the gizmoGrid state (`selected`, `tableState`, scroll position…).
    //   - 1 → "fresh data, never rendered" → `processAndLoadData` will
    //     create the gizmoGrid scaffold. We MUST take this path when the
    //     cached payload landed in the loader without anyone having
    //     processed it yet (cross-tab background fetch finished while
    //     another tab was active and the per-tab `uiSub` was on the other
    //     stream). Detected by `cached.selected` being absent — without
    //     this guard `ggApi.loadData` crashes on `undefined.color`.
    //
    // Legacy parity (GridService.js:192-193): `drillDownRefresh=1` means the
    // source tab called `workspaceFactory.clearCache(activeScreen)`, so a
    // second drill-down on the same target screen with a different predicate
    // value (e.g. different COMBINATION_KEY) must re-fetch instead of
    // replaying the previous row's cached payload.
    if (Number(activeScreen.commentLink?.drillDownRefresh) === 1) {
      forceRefresh = true;
    }

    const cached = this.tabGridLoader.getCached(activeScreen.screen_key);
    if (!forceRefresh && !hasPendingDrillDown && cached && this.isGridFetchSuccess(cached)) {
      cached.firstLoad = cached.selected ? 0 : 1;
      this.processAndLoadData(activeScreen, cached);
      return;
    }

    if (forceRefresh) {
      this.tabGridLoader.clearCache(activeScreen);
    }

    const routeConfig = this.appService.ROUTES?.[activeScreen.link];
    let apiGet: string | undefined = routeConfig?.api?.get;
    let legacyExtraParams: Record<string, any> | undefined;

    if (!apiGet) {
      // Legacy-only screens register their grid api via `$stateProvider` in the
      // AngularJS bundle and are never included in `appService.ROUTES` (the server
      // returns an empty `api.get` for them in `/getRoutes`). Fall back to a
      // migration map so the new `GridDirective` can still fetch the data itself,
      // otherwise the grid scaffold renders but stays empty (data is fetched by
      // legacy code but broadcast through `$scope.$broadcast`, which the Angular 12
      // `GridEventService` does not listen to).
      const legacyApi = LEGACY_ROUTE_API_GET[activeScreen.link];
      if (legacyApi) {
        apiGet = legacyApi.url;
        legacyExtraParams = legacyApi.params;
      } else {
        // Custom-tab / custom / pdf / tree screens render their own component
        // body (resolved by `WorkspaceScreenComponent.detectCustomElement`).
        // When such a screen has no migrated CE registered, the workspace
        // shell still renders `<gtw-grid>` as the fallback body and the
        // directive ends up here without an `api.get` — by design, because
        // there is no grid endpoint to call. Bail silently so we don't spam
        // the console with a warning that mis-suggests a missing-route bug
        // on grid screens. The `[GridDirective]` warning below is reserved
        // for screens whose `screen_type` indicates they really should be
        // loading grid data.
        const screenType = String(activeScreen.screen_type || '');
        const isGridScreenType =
          !screenType || screenType.startsWith('grid') || screenType === 'batch-save';
        if (!isGridScreenType) {
          this.ggApi?.hideLoader?.();
          this.workspaceService.gridLoading.next(false);
          this.workspaceService.dataFetched.next(true);
          return;
        }
        console.warn('[GridDirective] No api.get for route:', activeScreen.link);
        this.ggApi?.hideLoader?.();
        this.workspaceService.gridLoading.next(false);
        this.workspaceService.dataFetched.next(true);
        return;
      }
    }

    let fullUrl = apiGet;
    if (!fullUrl.startsWith('/') && !fullUrl.startsWith('http')) {
      fullUrl = environment.base_url + '/' + fullUrl;
    }

    const filterParams = activeScreen.filters?.getFilterParams
      ? activeScreen.filters.getFilterParams()
      : {};

    // For legacy-only screens, `activeScreen.legacyFilterParams` is populated by
    // the migration helper filter panel (e.g. `<gtw-submission-list-filter>`)
    // whenever the user hits "Filter". It takes precedence over the static
    // defaults from `LEGACY_ROUTE_API_GET` so that user picks actually apply.
    const legacyRuntimeParams = activeScreen.legacyFilterParams;

    // Drill-down context: commentLink.filters carry row-level values
    // (COMBINATION_KEY, SCHDM_DESCRIPTION, etc.) and screen-level filter
    // overrides (filter_key-matched). For standard drill-downs legacy
    // AngularJS applies row-level values client-side via colSearch
    // (table_state → processAndLoadData) and screen-level values through
    // the filter dropdowns (DataFiltersService.applyCommentLinkFilters →
    // getFilterParams), so nothing extra goes into the POST body.
    //
    // EXCEPTION (gridController.js:675-681): when the source column
    // declared `drilldown_filter_refresh=1`, legacy WRITES row-level
    // predicates into the POST body as lowercase keys (e.g.
    // `combination_key=2237198`). Targets like `screen_key=10102`
    // (1120 Sch C Drill-down, `filter_type: "closed"`) rely on this —
    // without those keys the backend can't resolve the row and returns
    // `data_size=0`. See the predicate injection block below.

    const params = _.merge(
      {},
      this.globalService.global?.globalParams || {},
      filterParams,
      // Default params for legacy-only routes (e.g. `type: 'federal'` which
      // `SubmissionListCtrl` hardcodes). Applied last so that anything coming
      // from filters / globals still wins if the user has actually picked a
      // value on screen.
      legacyExtraParams || {},
      // User-picked overrides from the legacy filter panel must win over the
      // static defaults above — place them last in the merge chain.
      legacyRuntimeParams || {},
    );

    if (params.tax_year && this.globalService.getScenario) {
      const resolvedScenario = this.globalService.getScenario(params.tax_year);
      if (resolvedScenario != null) {
        params.scenario = resolvedScenario;
      }
    }

    const includeIssueScenarioParams = this.isPdfIssueScenarioGridLoad(activeScreen, fullUrl);

    // Legacy GridFactory.getData only merged globalParams that existed in
    // the original AngularJS GlobalService (tax_year, scenario, jcd_key,
    // etc.). The migrated GlobalService.global.globalParams carries extra
    // keys that were never part of the legacy loadGridJson POST body.
    // Strip them so the payload matches legacy exactly. PDF DD/formbucket need
    // scenarioCode / is_issue_key_enabled for its load call.
    delete params.scenario_v1;
    if (!includeIssueScenarioParams) {
      delete params.scenarioCode;
      delete params.is_issue_key_enabled;
    }
    delete params.scenario_desc;

    // Legacy parity (gridController.js:675-681): drill-down columns flagged
    // `drilldown_filter_refresh=1` rewrite the target's POST body to the
    // row-level predicates captured in `commentLink.table_state`. Each
    // predicate becomes a lowercase key (`COMBINATION_KEY → combination_key`).
    // Done AFTER the merge so injected keys win over stale globals that
    // happened to share the name. Deletes the now-stale
    // `chart_filter`/`parent_me_string`/`filing_*` batch the target (closed
    // filter) does not accept.
    const activeCommentLink = activeScreen.commentLink;
    if (
      Number(activeCommentLink?.drillDownRefresh) === 1 &&
      (activeCommentLink?.type === 'drillDown' || activeCommentLink?.type === 'link') &&
      Array.isArray(activeCommentLink.table_state) &&
      activeCommentLink.table_state.length
    ) {
      for (const gf of activeCommentLink.table_state) {
        if (!gf?.predicate) continue;
        const key = String(gf.predicate).toLowerCase();
        const raw = gf.value;
        const val = Array.isArray(raw) ? raw.join(',') : raw;
        if (val !== undefined && val !== null && String(val).length) {
          params[key] = val;
        }
      }
      // Legacy only emitted the row-level predicates + the bare screen keys
      // (tax_year, scenario, jcd_key, …) for closed-filter drill-down
      // targets. Strip inherited batch filters that the target's `filter_type`
      // does not accept.
      if (activeScreen.filter_type === 'closed') {
        delete params.chart_filter;
        delete params.parent_me_string;
        delete params.filing_key;
        delete params.filing_label;
        delete params.filter_by;
        delete params.system;
      }
    }

    // Legacy parity: AngularJS GlobalService.globalParams always carried
    // parent_me_string from the global business panel. The Angular
    // GlobalService does not. When filterParams also omits it (ME filter
    // has no explicit selection), grid screens that need parent_me_string
    // receive empty data. Fall back to global_selected_me (set by the
    // header filing-group / business dropdown).
    //
    // `filter_type: "closed"` targets (e.g. screen 10102, 1120 Sch C Drill-
    // down) must NOT receive this fallback — legacy never sent
    // parent_me_string for closed screens, and the backend rejects it.
    //
    // CRITICAL: Skip this fallback if the screen has its OWN ME filter
    // (param_name === 'parent_me_string' or ui_type === 'df-me-drop-down').
    // For those screens, an empty `filter.selected` is the source of truth —
    // it means either "user did not pick anything" or "selection is being
    // reset by a cascade". Masking the empty state with the FULL list of
    // MEs from `global_selected_me` (header business panel) causes the
    // classic regression "I selected one ME but the grid shows data for
    // all MEs of the business". Legacy parity is preserved only for
    // screens that genuinely have no ME filter.
    if (!params.parent_me_string && activeScreen.filter_type !== 'closed') {
      const screenFilters = activeScreen.filters?.filters;
      // ME-equivalent filter detection — some screens implement their ME
      // selector under a non-canonical param_name. Without recognizing
      // these as the screen's own ME filter, the legacy fallback below
      // injects 88+ MEs from `global_selected_me` ON TOP of the screen's
      // single ME pick, and the backend AND-filter returns zero rows
      // (verified for `workspace.brus-gaap-amount`, grid_id=5250 where the
      // filter is `brus_parent_leid`).
      //
      // Only names z hard-evidence in code/runtime are listed:
      //   - parent_me_string, df-me-drop-down → preexisting check
      //   - brus_parent_leid → confirmed in BRUS GAAP Detail logs
      //   - fir_brus_leid → confirmed in BRUS Summary logs
      //   - fir_ho_leid → confirmed in HO/Branch GAAP Detail Network tab
      //   - fir_parent_leid → confirmed on screen 10165 (HO Branch
      //     Summary drill-down, grid_id=5113) — Network tab Form Data
      //     showed `fir_parent_leid=2075315` AND injected
      //     `parent_me_string` with full ME list, backend AND-filter
      //     returned empty rows.
      //   - ho_leid → fir-ep-drilldown.component.ts line 132
      //   - parent_leid → entityClassificationCtrl.js, ghostOwnership*
      //   - me_key → autoTagProcessCtrl.js, SystemSettingsFTCController.js
      // New BRUS-style / FIR-style screens with non-listed names need to
      // be added explicitly when they appear (intentionally conservative —
      // no wildcards to avoid catching unrelated `*_leid` audit/log
      // columns).
      const ME_LIKE_PARAMS = new Set([
        'parent_me_string',
        'ho_leid',
        'fir_ho_leid',
        'fir_parent_leid',
        'brus_parent_leid',
        'fir_brus_leid',
        'parent_leid',
        'me_key',
      ]);
      const hasOwnMeFilter = Array.isArray(screenFilters) && screenFilters.some(
        (f: any) => {
          const pn = String(f?.param_name || '').toLowerCase();
          if (ME_LIKE_PARAMS.has(pn)) return true;
          return f?.ui_type === 'df-me-drop-down';
        }
      );
      if (!hasOwnMeFilter) {
        const globalMes = this.globalService.global?.global_selected_me;
        if (globalMes) {
          const keys = this.collectMeKeys(globalMes);
          if (keys.length) {
            params.parent_me_string = keys.join(',');
          }
        }
      }
    }

    this.ggApi.showLoader();
    this.workspaceService.gridLoading.next(true);

    // Make sure the result-stream subscription is alive BEFORE we delegate
    // the fetch — the loader emits synchronously to its subject inside
    // `finalize` for cached HTTP responses, so the subscription must already
    // exist at that point.
    this.subscribeToTabResults();

    const isImportScreen = this.isImportScreenForFetch(activeScreen);

    if (activeScreen.split_call_param && activeScreen.filters?.filters) {
      const splitParam = activeScreen.split_call_param;
      const splitObj = _.find(activeScreen.filters.filters, { param_name: splitParam });
      if (splitObj?.selected && _.isArray(splitObj.selected)) {
        const filterArrays = splitObj.selected.map((meVal: any) => {
          const fp = _.cloneDeep(params);
          fp.parent_me_string = meVal;
          return fp;
        });
        this.tabGridLoader.fetchForScreen(activeScreen, fullUrl, params, {
          forceRefresh,
          isImportScreen,
          split: { filterArrays },
        });
        return;
      }
    }

    this.tabGridLoader.fetchForScreen(activeScreen, fullUrl, params, {
      forceRefresh,
      isImportScreen,
    });
  }

  /**
   * Subscribe to the per-tab result stream from `TabGridLoaderService`.
   * Idempotent: calling this multiple times for the same `currentScreenKey`
   * tears down the prior subscription first so we never have two listeners
   * racing on the same tab.
   */
  private subscribeToTabResults(): void {
    this.uiSub?.unsubscribe();
    this.uiSub = undefined;
    if (!this.currentScreenKey) return;
    this.uiSub = this.tabGridLoader
      .getResultStream(this.currentScreenKey)
      .subscribe((result: TabGridResult) => this.handleTabGridResult(result));
  }

  private handleTabGridResult(result: TabGridResult): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    // Render only if the user is still on this tab. If they switched away
    // before the response landed, the data is already cached on the screen
    // object (loader's finalize()) and will be picked up by getCached() on
    // their next visit — so silently dropping the render here is correct.
    if (
      !activeScreen ||
      String(activeScreen.screen_key) !== String(result.screenKey)
    ) {
      return;
    }
    if (!this.ggApi) {
      this.workspaceService.gridLoading.next(false);
      return;
    }
    if (result.error) {
      this.handleGridLoadError(
        `load failed for "${activeScreen.label || activeScreen.screen_key}"`,
        result.error,
        activeScreen
      );
      return;
    }
    if (result.data) {
      this.processAndLoadData(activeScreen, result.data);
    }
  }

  private processAndLoadData(activeScreen: any, data: any): void {
    activeScreen.loading_data = false;
    this.ensureParsedGridSettings(data);
    this.coerceImportGridTypeForGizmo(activeScreen, data);
    this.normalizeImportEditableHeaders(activeScreen, data);
    this.restoreGridHeadersIfMissing(activeScreen, data);
    if (Array.isArray(data.header)) {
      this.ensureMinimumDisplayedHeaders(
        data.header,
        2,
        Array.isArray(data.data) && data.data.length > 0
      );
    }
    // Mark the dot orange on the CURRENT screen reference. Important for
    // the cache-hit fast path after a workspace re-mount: the loader's
    // `finalize` only ever flipped `cachedData` on whatever screen ref
    // was passed to `fetchForScreen`, so a brand-new screen object that
    // never went through fetch (because the data was served from the
    // singleton cache) would otherwise stay gray.
    activeScreen.cachedData = true;

    // Whenever we actually render data (cache hit OR a fresh response from
    // `TabGridLoaderService`) the workspace MUST consider data "fetched" so
    // the `*ngIf="!dataFetched && !gridLoading"` placeholder hides and the
    // grid container's `gt-grid-hidden` class is removed. Without this, a
    // tab-return that hits the in-flight branch leaves dataFetched=false
    // (DataFilters' tab-return path emitted false because no cache existed
    // yet) and the rendered grid stays display:none behind the "Please use
    // filters" placeholder.
    if (!this.workspaceService.dataFetched.value) {
      this.workspaceService.dataFetched.next(true);
    }

    if (data.settings) {
      data.settings.hasPivot =
        data.settings.grid_type === 'import' || data.settings.grid_type === 'custom' ? 0 : 1;
      try {
        data.settings.batch_select_rules = data.settings.batch_select_rules
          ? (typeof data.settings.batch_select_rules === 'string'
            ? JSON.parse(data.settings.batch_select_rules) : data.settings.batch_select_rules)
          : '';
      } catch (_e) { data.settings.batch_select_rules = ''; }
      data.settings.commentType = 'all';
    }

    // Pick up the snapshot of the user's filter chips / sort / global
    // search that `loadGridData` captured BEFORE the loader pipeline
    // mutated `activeScreen.data`. We cannot read it from
    // `activeScreen.data?.tableState` here because by the time
    // `processAndLoadData` runs, the previous data has already been
    // replaced — `clearCache` nulled it and `TabGridLoaderService.finalize`
    // reassigned it to the freshly fetched payload. The transient
    // property survives that pipeline and is consumed (deleted) below
    // after we re-apply the chips on the new payload.
    //
    // Drill-down/link navigations are handled at capture time
    // (`captureTableStateForReload` bails for them) so the drill-down
    // injection a few lines further down is the sole source of truth
    // for those flows.
    const priorFilterState: {
      colSearch: any[];
      sort: any;
      globalValue: any;
    } | null = activeScreen._preservedTableStateOnReload || null;
    if (activeScreen._preservedTableStateOnReload) {
      delete activeScreen._preservedTableStateOnReload;
    }

    if (data.firstLoad == null || data.firstLoad === 1) {
      data.batchSelect = [];
      data.selected = {
        color: 'green', select_type: null, row_id: null, col_id: null,
        obj_id: null, data_type: null, edit_type: null, validation: null,
        action: null, format: null, value: null, oldValue: null,
        rowData: null, editable: false, element: null,
      };
      data.tableState = {
        sort: {}, search: {}, colSearch: [],
        pagination: { start: 0 },
      };
      data.cellData = {};
      data.vDragg = { draggerPositionY: 0, scrollPositionY: 0, startIndex: 0, endIndex: 0 };
      data.hDragg = { draggerPositionX: 0, scrollPositionX: 0, startIndex: 0, endIndex: 0, maxStartIndex: 0 };
    }
    data.screen_key = activeScreen.screen_key;

    // Dual-grid raw-data screens (gridController.js lines 787-813):
    // screens 11169 and 11471 display data uploaded via the feed import toolbar
    // (GlobalService.setRawData / setpRawData). If the user opens the grid before
    // uploading a feed, the server returns an empty/stub response, so we substitute
    // the in-memory raw data. When none has been loaded yet, warn and show an empty grid.
    const screenKeyStr = String(data.screen_key ?? '');
    if (screenKeyStr === '11169') {
      const rawData = this.globalService.getRawData();
      if (rawData && rawData.length > 1) {
        data.data = rawData;
      } else {
        data.data = [];
        this.gtwSnackbarService.openSnackBar(
          'This Grid is only for displaying raw data, Kindly load the feed data first',
          'gtw-snackbar--yellow'
        );
      }
    }

    if (data.firstLoad == null) {
      data.firstLoad = 1;
    }

    // Drill-down / link parity with legacy gridController.js (lines 616-617, 841):
    // inject the source-row filters (gridFilters) built by handleDrillDown /
    // handleLink into the grid's colSearch so gizmoGrid.buildFilterFunctions()
    // renders them as chips in gg-footer-filters (the gray bar near
    // "Total Records") and filters rows client-side.
    const drillType = activeScreen.commentLink?.type;
    console.log('[GridDirective] processAndLoadData: commentLink type:', drillType, 'table_state:', activeScreen.commentLink?.table_state?.length, '_drillDownColSearch:', activeScreen._drillDownColSearch?.length);
    if (drillType === 'drillDown' || drillType === 'link') {
      const dcFilters = activeScreen.commentLink.table_state;
      if (Array.isArray(dcFilters) && dcFilters.length) {
        if (!data.tableState) {
          data.tableState = { sort: {}, search: {}, colSearch: [], pagination: { start: 0 } };
        }
        data.tableState.colSearch = this.normalizeColSearchTypes(dcFilters, data);
        console.log('[GridDirective] processAndLoadData: injected colSearch count:', data.tableState.colSearch.length, 'predicates:', dcFilters.map((f: any) => f.predicate));
      }
    } else if (activeScreen._drillDownColSearch?.length) {
      // Re-apply saved colSearch from a previous drill-down load. After
      // the filter cascade completes, setTabActive re-fires and the grid
      // reinits — but commentLink was already cleared on the first load.
      // The saved state ensures the chips survive the reinit.
      if (!data.tableState) {
        data.tableState = { sort: {}, search: {}, colSearch: [], pagination: { start: 0 } };
      }
      data.tableState.colSearch = this.normalizeColSearchTypes(activeScreen._drillDownColSearch, data);
      delete activeScreen._drillDownColSearch;
    }

    // Restore the user-applied filters / sort / global search captured
    // before the firstLoad reset above. Without this restore, every
    // `loadGridData(_, true)` refresh (modal save, refreshGridData.next,
    // batch action, CeHostDialog 'refresh' close, ...) silently drops the
    // footer chips and resets sort order. Drill-down/link navigations
    // are skipped at snapshot time (priorFilterState stays null), so the
    // drill-down injection above wins for those flows.
    //
    // Defensive guards:
    //   - `validPredicateSet` filters out chips whose column no longer
    //     exists in the new payload (e.g. top filter panel parameters
    //     changed the column shape). Restoring stale predicates would
    //     produce a "0 rows" state that looks like data loss.
    //   - Sort is only restored when its predicate also exists in the new
    //     headers, otherwise gizmoGrid would render "undefined : A → Z".
    //   - `normalizeColSearchTypes` coerces filter values to the types of
    //     the new payload's columns (re-using the helper used by the
    //     drill-down path), avoiding strict-equality mismatches in
    //     gizmoGrid's pipe().
    if (priorFilterState) {
      if (!data.tableState) {
        data.tableState = { sort: {}, search: {}, colSearch: [], pagination: { start: 0 } };
      }
      const validPredicateSet = new Set(
        (data.header || []).map((h: any) => String(h?.map))
      );
      const survivingColSearch = priorFilterState.colSearch.filter(
        (f: any) => f?.predicate && validPredicateSet.has(String(f.predicate))
      );
      if (survivingColSearch.length) {
        data.tableState.colSearch = this.normalizeColSearchTypes(survivingColSearch, data);
      }
      if (
        !_.isEmpty(priorFilterState.sort) &&
        priorFilterState.sort.predicate &&
        validPredicateSet.has(String(priorFilterState.sort.predicate))
      ) {
        data.tableState.sort = priorFilterState.sort;
      }
      if (priorFilterState.globalValue != null && priorFilterState.globalValue !== '') {
        data.tableState.search = {
          ...(data.tableState.search || {}),
          globalValue: priorFilterState.globalValue,
        };
      }
    }

    this.processTemplates(data);

    activeScreen.data = data;

    this.injectIssueColumns(activeScreen, data);
    this.injectLockYnHeaderStyle(data);

    data.data?.forEach((row: any) => {
      row.SCENARIO_TYPE_CODE = data.settings?.display_issue_columns === 1 ? 'EXAM' : '';
    });

    const ggData: any = {};
    ggData.headerArray = data.header || [];

    if (data.firstLoad === 0) {
      ggData.dataArr = data.data?.length > 0
        ? _.each(data.data, (item: any) => { item.isFiltered = 'N'; })
        : data.data || [];

      if (
        (data.pivotRowList?.length > 0) ||
        (data.pivotValList?.length > 0) ||
        (data.pivotColumn?.length > 0)
      ) {
        this.gridEventService.broadcast('applyPivotPanel', data.pivotGroup, false);
      } else if (
        data.pivotListDelTriggered === true
      ) {
        this.gridEventService.broadcast('applyPivotPanel', data.pivotGroup, false);
      } else {
        this.gridEventService.broadcast('applyPivotPanel', data.pivotGroup, true);
      }
    } else {
      ggData.dataArr = data.data || [];
      ggData.pivotGroup = {};
    }

    ggData.settings = data.settings;
    ggData.tableState = data.tableState;
    ggData.cellData = data.cellData;
    ggData.selected = data.selected;
    ggData.vDragg = data.vDragg;
    ggData.hDragg = data.hDragg;
    ggData.batchSelect = data.batchSelect || [];
    ggData.columnTemplates = data.columnTemplates || [];
    ggData.actionTemplates = data.actionTemplates || [];
    if (activeScreen.data) {
      activeScreen.data.actionTemplates = ggData.actionTemplates;
    }
    ggData.pivotActionTemplates = [];
    ggData.firstLoad = data.firstLoad;
    ggData.pivotGroup = data.pivotGroup || {};
    ggData.messageObject = [];
    ggData.userAccessLevel = 1;
    ggData.sso_id = '';

    const selectedActionTemplate = _.filter(data.actionTemplates, { selectedAction: true });
    if (!selectedActionTemplate.length) {
      this.ggApi?.clearSlectedActionTemplateFirst?.();
    }

    try {
      const settings = JSON.parse(sessionStorage.getItem('user_settings') || '{}');
      ggData.userAccessLevel = settings?.system_access_level || 1;
      ggData.sso_id = settings?.sso_id || '';
    } catch (_e) { /* ignore */ }

    for (let i = 0; i < ggData.headerArray.length; i++) {
      ggData.headerArray[i].object_id = i.toString();
    }

    if (
      data.pivotGroup && !_.isEmpty(data.pivotGroup) &&
      ((data.pivotGroup.columnToRows?.length > 0) || (data.pivotGroup.groupRows?.length > 0)) &&
      activeScreen.data?.pivotdataArr?.length > 0
    ) {
      ggData.dataArr = activeScreen.data.pivotdataArr;
      ggData.headerArray = activeScreen.data.pivotheaderArray;
      ggData.totalRecords = activeScreen.data.data?.length;
    }

    // gizmoGrid's internal `setDefaultSelect()` needs at least one visible
    // header (prefers the second). Batch-import reloads can arrive with too
    // few `display:'y'` columns — normalize before handing off to gizmoGrid.
    if (ggData.dataArr?.length > 0) {
      this.ensureMinimumDisplayedHeaders(ggData.headerArray, 2, true);
    }

    this.ggApi.hideOverLay?.();

    if (!ggData.dataArr || ggData.dataArr.length === 0) {
      this.ggApi.hideLoader?.();
      this.workspaceService.gridLoading.next(false);
      this.ggApi.loadData({ data: ggData });
      this.scheduleGridRefresh();

      // gizmoGrid's loadGrid() early-returns on empty data and never rebuilds
      // the footer filter chips, so any stale chips left over from a previous
      // (non-empty) state would remain visible with mismatched/empty labels.
      // Wipe them defensively.
      this.ggGrid?.find('.gg-filter-container').empty();

      const isImportScreen = this.isImportLikeGrid(activeScreen, data);
      if (!isImportScreen) {
        this.ggGrid?.find('.gg-alert').remove();
        this.gtwSnackbarService.openSnackBar(
          'No data found with this filter criteria.',
          'gtw-snackbar--yellow'
        );
      }
      return;
    }

    // Remove gt-grid-hidden (display:none) BEFORE gizmoGrid's loadData so
    // the jQuery plugin can measure real container dimensions during its
    // initial render pass. Without this, gizmo calculates zero-width columns
    // and the data rows are invisible even though the data is loaded.
    this.workspaceService.gridLoading.next(false);
    this.ggApi.loadData({ data: ggData });
    this.scheduleGridRefresh();

    if (data.settings?.commentType) {
      const type = data.settings.commentType || 'all';
      this.loadMessages(type);
    }

    if (activeScreen.actionGrid) {
      setTimeout(() => {
        if (activeScreen.actionGrid != null) {
          this.ggApi?.goToActionTemplate?.(activeScreen.actionGrid);
        }
        activeScreen.actionGrid = null;
      }, 300);
    }

    if (activeScreen.commentLink) {
      if (activeScreen.commentLink.type === 'comment') {
        const table_state = activeScreen.commentLink.table_state || null;
        const col_id = activeScreen.commentLink.locationObj?.col_key || null;
        const row_id = activeScreen.commentLink.locationObj?.row_key || null;
        const type = activeScreen.commentLink.type || 'link';

        setTimeout(() => {
          this.ggApi?.goToComment?.({ type, col_id, row_id, tableState: table_state });
          if (activeScreen.data) {
            activeScreen.data.tableState = table_state;
          }
          activeScreen.commentLink = null;
        }, 300);
      } else if (
        activeScreen.commentLink.type === 'drillDown' ||
        activeScreen.commentLink.type === 'link'
      ) {
        // Save the colSearch chips so they survive a grid reinit triggered
        // by the post-cascade setTabActive (commentLink will be null by then).
        if (data.tableState?.colSearch?.length) {
          activeScreen._drillDownColSearch = _.cloneDeep(data.tableState.colSearch);
        }
        activeScreen.commentLink = null;
      }
      if (activeScreen.commentLink?.type != null) {
        activeScreen.commentLink.filters = null;
      }
    }

    if (activeScreen.data?.firstLoad === 1 &&
        activeScreen.screen_type === 'batch-save') {
      this.gridEventService.broadcast('batchSaveLoaded', data);
    }
  }

  /** Workspace `grid-import*` tabs or any screen whose payload uses `settings.grid_type === 'import'` (e.g. E-File Import Keys). */
  private isImportLikeGrid(activeScreen: any, data: any): boolean {
    if (this.isImportWorkspaceScreen(activeScreen)) {
      return true;
    }
    if (
      activeScreen?.importScreen != null
      && typeof activeScreen.importScreen === 'object'
    ) {
      return true;
    }
    this.ensureParsedGridSettings(data);
    const gt = String(
      data?.settings?.grid_type ?? data?.settings?.GRID_TYPE ?? ''
    ).toLowerCase();
    return gt === 'import';
  }

  /**
   * Normalized `screen_type` / `SCREEN_TYPE` (trim + lower-case) so backend
   * casing variants still match gizmoGrid + TabGridLoader import behaviour.
   */
  private normalizeWorkspaceScreenType(activeScreen: any): string {
    const raw = activeScreen?.screen_type ?? activeScreen?.SCREEN_TYPE ?? '';
    return String(raw).trim().toLowerCase();
  }

  private isImportWorkspaceScreen(activeScreen: any): boolean {
    const st = this.normalizeWorkspaceScreenType(activeScreen);
    return GridDirective.IMPORT_GRID_SCREEN_TYPES.includes(st);
  }

  /**
   * True when this fetch should use import-specific handling (placeholder-row
   * stripping in `TabGridLoaderService`, etc.). Includes `importScreen` once
   * the toolbar has initialised it so we do not depend on subscription order.
   */
  private isImportScreenForFetch(activeScreen: any): boolean {
    return (
      this.isImportWorkspaceScreen(activeScreen)
      || (
        activeScreen?.importScreen != null
        && typeof activeScreen.importScreen === 'object'
      )
    );
  }

  /** If the backend JSON-stringified `settings`, parse so `grid_type` checks work. */
  private ensureParsedGridSettings(data: any): void {
    if (!data || typeof data.settings !== 'string') {
      return;
    }
    try {
      data.settings = JSON.parse(data.settings);
    } catch {
      data.settings = {};
    }
  }

  /**
   * gizmoGrid `loadGrid` treats missing `settings.grid_type` as non-import and
   * shows "No Data found with this filter criteria" before it can inject the
   * placeholder row. Align with legacy when we already know this is an import tab.
   */
  private coerceImportGridTypeForGizmo(activeScreen: any, data: any): void {
    if (!this.isImportScreenForFetch(activeScreen)) {
      return;
    }
    if (!data.settings || typeof data.settings !== 'object') {
      data.settings = {};
    }
    if (String(data.settings.grid_type || '').toLowerCase() !== 'import') {
      data.settings.grid_type = 'import';
    }
  }

  private normalizeImportEditableHeaders(activeScreen: any, data: any): void {
    if (!this.isImportLikeGrid(activeScreen, data) || !Array.isArray(data?.header)) {
      return;
    }

    data.header.forEach((header: any) => {
      const map = String(header?.map || '').toLowerCase();
      if (map === 'object_id' || map === 'error_msg' || map === 'validated') {
        header.editable = 'n';
        return;
      }

      if (this.isEditableFlag(header?.editable) || this.isEditableFlag(header?.new_row_editable)) {
        header.editable = 'y';
        header.edit_type = header.edit_type || 'text';
      }
    });
  }

  private isEditableFlag(value: any): boolean {
    if (value === true || value === 1) {
      return true;
    }
    const normalized = String(value ?? '').trim().toLowerCase();
    return normalized === 'y' || normalized === 'yes' || normalized === 'true' || normalized === '1';
  }

  /**
   * True on the empty template before the user loads real rows (upload / paste / batch).
   * Uses toolbar `importScreen.step` when present; otherwise treats a single placeholder row
   * as pre-upload for import-like grids without `importScreen` (some E-File flows).
   */
  private isImportPrefilePhase(activeScreen: any, data: any): boolean {
    if (!this.isImportLikeGrid(activeScreen, data)) {
      return false;
    }
    const step = activeScreen?.importScreen?.step;
    if (step != null && step >= 2) {
      return false;
    }
    if (activeScreen?.importScreen != null) {
      return step == null || step < 2;
    }
    const rows = data?.data;
    if (!Array.isArray(rows) || rows.length !== 1) {
      return false;
    }
    const row = rows[0] || {};
    const meaningful = Object.keys(row).filter((k) => {
      const v = row[k];
      if (v == null || v === '') {
        return false;
      }
      if (k === 'object_id' && String(v) === '1') {
        return false;
      }
      return true;
    });
    return meaningful.length === 0;
  }

  /**
   * gizmoGrid `buildFilterFunctions` (gizmoGrid.js) renders `.gg-footer-filters` chips from
   * `tableState.sort` / `colSearch` / etc. Import screens must keep that footer empty until
   * upload (`importScreen.step >= 2`). Also fixes `sort.predicate` without legacy `lable`,
   * which renders as "undefined : A → Z".
   */
  private applyGridFooterTableStateGuards(activeScreen: any, data: any): void {
    if (!data) {
      return;
    }

    const importBeforeUpload = this.isImportPrefilePhase(activeScreen, data);

    if (!data.tableState) {
      data.tableState = { sort: {}, search: {}, colSearch: [], pagination: { start: 0 } };
    }

    if (importBeforeUpload) {
      data.tableState.sort = {};
      data.tableState.colSearch = [];
      if (data.tableState.search) {
        data.tableState.search.globalValue = null;
      }
      delete data.tableState.comments;
      delete data.tableState.attachments;
      delete data.tableState.auditCells;
      return;
    }

    this.patchGizmoSortLabelFromHeader(data);
  }

  /** gizmoGrid reads `sort.lable` (typo); backend may only send `label` or omit both. */
  private patchGizmoSortLabelFromHeader(data: any): void {
    const sort = data?.tableState?.sort;
    if (!sort?.predicate) {
      return;
    }
    const existing = sort.lable ?? sort.label;
    if (existing && String(existing).trim()) {
      sort.lable = existing;
      return;
    }
    const headers = Array.isArray(data?.header) ? data.header : [];
    const col = _.find(headers, (h: any) => String(h?.map) === String(sort.predicate));
    const resolved = col?.label || col?.full_label;
    if (resolved) {
      sort.lable = resolved;
    } else {
      data.tableState.sort = {};
    }
  }

  /**
   * Import pre-upload: hide footer filter chips and column header filter/sort dropdowns
   * (`.gg-dropMenu`) until real data is loaded.
   */
  private syncImportGridFooterVisibility(activeScreen: any): void {
    if (!this.ggGrid?.length) {
      return;
    }
    const data = activeScreen?.data;
    const isImportLike = this.isImportLikeGrid(activeScreen, data);
    if (!isImportLike) {
      this.ggGrid.find('.gg-footer-filters').css('display', '');
      this.ggGrid.find('.gg-header .gg-dropMenu').css('display', '');
      return;
    }
    const beforeUpload = this.isImportPrefilePhase(activeScreen, data);
    this.ggGrid.find('.gg-footer-filters').css('display', beforeUpload ? 'none' : '');
    this.ggGrid.find('.gg-header .gg-dropMenu').css('display', beforeUpload ? 'none' : '');
  }

  /**
   * Snapshot the user's column filter chips, sort and global-search input
   * onto a transient `_preservedTableStateOnReload` property of the active
   * screen so they survive a forced refresh.
   *
   * Why on the screen and not as a directive field: `TabGridLoaderService`
   * keys the in-flight fetch by `screen_key` and may complete in the
   * background even if the user is on a different tab when the response
   * lands. Pinning the snapshot to the screen object means it travels with
   * the data through the loader pipeline (`clearCache` → fetch → `finalize`
   * → `processAndLoadData`) without us having to re-key in the directive.
   *
   * Bypass conditions:
   *   - Drill-down/link navigation (commentLink type drillDown/link): the
   *     drill-down injection at `processAndLoadData:921-929` is the source
   *     of truth for colSearch on those entries, and snapshotting the
   *     previous screen's chips would clobber the drill-down filters.
   *   - No previous data or empty state: nothing to preserve, leave the
   *     property unset so the restore branch is a no-op.
   */
  private captureTableStateForReload(activeScreen: any): void {
    if (!activeScreen) return;
    const isDrillDownNav =
      activeScreen.commentLink?.type === 'drillDown' ||
      activeScreen.commentLink?.type === 'link';
    if (isDrillDownNav) {
      delete activeScreen._preservedTableStateOnReload;
      return;
    }
    const prev = activeScreen.data?.tableState;
    if (!prev) {
      delete activeScreen._preservedTableStateOnReload;
      return;
    }
    const hasColSearch = Array.isArray(prev.colSearch) && prev.colSearch.length > 0;
    const hasSort = prev.sort && !_.isEmpty(prev.sort);
    const hasGlobalSearch =
      prev.search?.globalValue != null && prev.search.globalValue !== '';
    if (!hasColSearch && !hasSort && !hasGlobalSearch) {
      delete activeScreen._preservedTableStateOnReload;
      return;
    }
    activeScreen._preservedTableStateOnReload = {
      colSearch: hasColSearch ? _.cloneDeep(prev.colSearch) : [],
      sort: hasSort ? _.cloneDeep(prev.sort) : {},
      globalValue: hasGlobalSearch ? prev.search.globalValue : null,
    };
  }

  /**
   * Coerce colSearch `value[]` entries to the data type of the target grid's
   * actual row data. Drill-down filters come from the source row's rowData,
   * and the backend is inconsistent: pivot grids may emit COMBINATION_KEY as
   * a string while the target detail grid emits it as number. gizmoGrid's
   * pipe() compares with strict equality (`_.findByValues`), so a type
   * mismatch silently filters the grid down to zero rows.
   */
  private normalizeColSearchTypes(filters: any[], data: any): any[] {
    const sampleRow = data?.data?.[0];
    const cloned = _.cloneDeep(filters);
    if (!sampleRow) return cloned;
    return cloned.map((f: any) => {
      if (!f || !Array.isArray(f.value) || f.value.length === 0) return f;
      const refVal = sampleRow[f.predicate];
      if (refVal == null) return f;
      const targetType = typeof refVal;
      if (targetType !== 'number' && targetType !== 'string') return f;
      const needsCoerce = f.value.some((v: any) => typeof v !== targetType);
      if (!needsCoerce) return f;
      f.value = f.value.map((v: any) => {
        if (targetType === 'number') {
          const n = Number(v);
          return Number.isNaN(n) ? v : n;
        }
        return String(v);
      });
      return f;
    });
  }

  private processTemplates(data: any): void {
    if (!data.header) return;

    _.forEach(data.header, (value: any) => {
      if (value.edit_rules && typeof value.edit_rules === 'string') {
        try {
          value.edit_rules = JSON.parse(value.edit_rules);
        } catch (_e) { /* ignore */ }
      }
    });

    if (data.columnTemplates) {
      _.forEach(data.columnTemplates, (value: any) => {
        if (typeof value.columns === 'string') {
          try {
            value.columns = JSON.parse(value.columns);
          } catch (_e) {
            value.columns = '';
          }
        }
      });
    } else {
      data.columnTemplates = [];
    }

    if (data.header?.length) {
      const templateArr = data.header.map((header: any) => ({
        width: header.width,
        display: header.display,
        map: header.map,
      }));
      const defaultTemplate = {
        name: 'Default Columns',
        template_key: 0,
        system: 1,
        default_template: 0,
        columns: templateArr,
      };
      const hasDefault = data.columnTemplates.some((t: any) => t.template_key === 0);
      if (!hasDefault) {
        data.columnTemplates.unshift(defaultTemplate);
      }
    }
  }

  private injectIssueColumns(activeScreen: any, data: any): void {
    if (!data.settings || data.settings.display_issue_columns !== 1) return;
    if (!data.header) return;

    if (
      (data.settings.grid_type === 'batch-save' || data.settings.grid_type === 'dynamic') &&
      data.firstLoad === 1
    ) {
      const issueIdx = _.findIndex(data.header, { map: 'ISSUE_KEY' });
      if (issueIdx > -1) data.header.splice(issueIdx, 1);
      const stdIdx = _.findIndex(data.header, { map: 'STD_DESC' });
      if (stdIdx > -1) data.header.splice(stdIdx, 1);
    }

    if (data.settings.grid_type === 'import' && activeScreen.screen_key !== '11237') {
      if (data.header.filter((i: any) => i.map === 'issue_key').length === 0) {
        data.header.push({
          data_type: 'number', display: 'y', editable: 'y', label: 'Issue Id',
          map: 'issue_key', validation: 'numeric', call_back: 'rowValidation',
          edit_type: 'text', width: 90, header_class: 'issueColumns', required: '1',
        });
      }
    } else if (
      data.settings.grid_type !== 'import' &&
      data.header.filter((i: any) => i.map === 'ISSUE_KEY').length < 1
    ) {
      const index = data.settings.fixed_columns || 0;
      const screenType = activeScreen.screen_type;

      const issueKeyHeader: any = {
        data_type: (screenType === 'grid-dynamic' || screenType === 'grid-standard-pivot')
          ? 'string' : 'number',
        display: 'y', editable: 'n', label: 'Issue Id', map: 'ISSUE_KEY',
        validation: null, width: 90, header_class: 'issueColumns', object_id: 'issueId',
      };
      const stdDescHeader: any = {
        data_type: 'string', display: 'y', editable: 'n', label: 'QAR Standard Description',
        map: 'STD_DESC', validation: null, width: 90, header_class: 'issueColumns', object_id: 'stdDesc',
      };
      const issueDescHeader: any = {
        data_type: 'string', display: 'y', editable: 'n', label: 'Issue Description',
        map: 'ISSUE_DESC', validation: null, width: 90, header_class: 'issueColumns', object_id: 'issueDesc',
      };
      const shortDescHeader: any = {
        data_type: 'string', display: 'y', editable: 'n', label: 'Issue Short Description',
        map: 'ISSUE_SHORT_DESC', validation: null, width: 90, header_class: 'issueColumns', object_id: 'shortDesc',
      };

      data.header.splice(index + 1, 0, issueKeyHeader);
      data.header.splice(index + 2, 0, stdDescHeader);
      data.header.splice(index + 3, 0, issueDescHeader);
      data.header.splice(index + 4, 0, shortDescHeader);

      const colIssueKey = { display: 'y', map: 'ISSUE_KEY', width: 90 };
      const colStdDesc = { display: 'y', map: 'STD_DESC', width: 90 };
      const colIssueDesc = { display: 'y', map: 'ISSUE_DESC', width: 90 };
      const colShortDesc = { display: 'y', map: 'ISSUE_SHORT_DESC', width: 90 };

      if (data.columnTemplates?.length > 0) {
        _.each(data.columnTemplates, (_v: any, k: number) => {
          if (data.columnTemplates[k].columns && _.isArray(data.columnTemplates[k].columns)) {
            data.columnTemplates[k].columns.splice(index + 1, 0, colIssueKey);
            data.columnTemplates[k].columns.splice(index + 2, 0, colStdDesc);
            data.columnTemplates[k].columns.splice(index + 3, 0, colIssueDesc);
            data.columnTemplates[k].columns.splice(index + 4, 0, colShortDesc);
          }
        });
      }

      data.settings.fixed_columns = (data.settings.fixed_columns || 0) + 4;
    }
  }

  private injectLockYnHeaderStyle(data: any): void {
    if (!Array.isArray(data?.header) || data.header.length === 0) return;

    data.header.forEach((header: any) => {
      const map = String(header?.map || '').toUpperCase();
      if (map !== 'LOCK_Y_N') return;

      const currentClass = String(header?.header_class || '').trim();
      if (!currentClass.includes('lock-yn-header')) {
        header.header_class = currentClass
          ? `${currentClass} lock-yn-header`
          : 'lock-yn-header';
      }
    });
  }

  private loadMessages(type: string): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    if (!activeScreen) return;

    const filterCached = activeScreen.filters?.getFilterParamsFromCache
      ? activeScreen.filters.getFilterParamsFromCache()
      : {};

    if (!filterCached) {
      this.ggApi?.loadComments?.([], 'none');
      return;
    }

    const params: any = {
      type,
      filters: JSON.stringify(filterCached),
      screen_key: activeScreen.screen_key,
    };

    this.http.post<any>(environment.base_url + '/getCommentByContext', params)
      .pipe(take(1))
      .subscribe(
        (data: any) => {
          if (data?.callSuccess === '1') {
            this.ggApi?.loadComments?.(data.commentLocations, type);
            this.ggApi?.loadAuditCells?.(data.auditLocations, 'grid');
          }
        },
        (err: any) => console.error('[GridDirective] loadMessages error:', err)
      );
  }

  private loadAttachments(): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    if (!activeScreen) return;

    const filterCached = activeScreen.filters?.getFilterParamsFromCache
      ? activeScreen.filters.getFilterParamsFromCache()
      : {};

    const params: any = {
      filters: JSON.stringify(filterCached),
      screen_key: activeScreen.screen_key,
    };

    this.http.post<any>(environment.base_url + '/getAttachmentByContext', params)
      .pipe(take(1))
      .subscribe(
        (data: any) => {
          if (data?.callSuccess === '1') {
            this.ggApi?.loadAttachments?.(data.attachmentLocations);
          }
        },
        (err: any) => console.error('[GridDirective] loadAttachments error:', err)
      );
  }

  private setupAlertObserver(): void {
    const container: HTMLElement | null =
      this.ggGrid?.[0]?.querySelector('.gg-alert-container');
    if (!container) return;

    this.alertObserver?.disconnect();
    this.alertObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (!(node instanceof HTMLElement) || !node.classList.contains('gg-alert')) continue;

          const message = node.querySelector('.gg-alert-message')?.textContent?.trim() || '';
          node.remove();

          if (message) {
            const panelClass =
              node.classList.contains('alert-error') || node.classList.contains('alert-danger')
                ? 'gtw-snackbar--red'
                : node.classList.contains('alert-success')
                  ? 'gtw-snackbar--green'
                  : node.classList.contains('alert-info')
                    ? 'gtw-snackbar--yellow'
                    : 'gtw-snackbar--orange';

            this.gtwSnackbarService.openSnackBar(message, panelClass);
          }
        }
      }
    });

    this.alertObserver.observe(container, { childList: true });
  }

  private destroyGrid(): void {
    this.alertObserver?.disconnect();
    this.alertObserver = null;
    // NOTE: do NOT cancel any in-flight grid fetch here — the HTTP
    // subscription is owned by `TabGridLoaderService` precisely so that
    // switching tabs (which always calls into here) keeps background
    // requests alive. We only tear down the UI-side stream subscription so
    // the destroyed grid stops trying to render into a torn-down jQuery
    // instance; it will be re-subscribed in `loadGridData` for the new tab.
    this.uiSub?.unsubscribe();
    this.uiSub = undefined;
    if (this.ggGrid) {
      this.ggGrid.off('click.exportAction');
      this.ggGrid.off('click.filteredExportSync');
      this.ggGrid.off('mousewheel.ggMultiSelect DOMMouseScroll.ggMultiSelect');
    }
    if (this.ggApi?.destroy) {
      this.ggApi.destroy();
      this.ggApi = null;
    }
    this.gridInitialized = false;
  }

  private restoreGridHeadersIfMissing(activeScreen: any, data: any): void {
    if (Array.isArray(data?.header) && data.header.length > 0) {
      return;
    }
    const cached = this.tabGridLoader.getCached(activeScreen?.screen_key);
    if (cached?.header?.length) {
      data.header = _.cloneDeep(cached.header);
      if (!data.settings && cached.settings) {
        data.settings = _.cloneDeep(cached.settings);
      }
    }
  }

  private isHeaderDisplayed(header: any): boolean {
    const display = String(header?.display ?? 'y').trim().toLowerCase();
    return display === 'y' || display === 'yes' || display === 'true' || display === '1';
  }

  /**
   * gizmoGrid `setDefaultSelect()` reads the second visible column when present.
   * Promote headers to `display:'y'` when batch/import reloads expose too few.
   */
  private ensureMinimumDisplayedHeaders(
    headerArray: any[],
    minVisible = 2,
    onlyWhenDataPresent = false
  ): void {
    if (!Array.isArray(headerArray) || headerArray.length === 0) {
      return;
    }

    let visibleCount = headerArray.filter((header) => this.isHeaderDisplayed(header)).length;
    if (visibleCount >= minVisible) {
      return;
    }
    if (onlyWhenDataPresent && visibleCount === 0 && headerArray.length === 0) {
      return;
    }

    for (const header of headerArray) {
      if (visibleCount >= minVisible) {
        break;
      }
      if (!header?.map || this.isHeaderDisplayed(header)) {
        continue;
      }
      header.display = 'y';
      visibleCount++;
    }

    for (const header of headerArray) {
      if (visibleCount >= minVisible) {
        break;
      }
      if (!header?.map) {
        continue;
      }
      if (!this.isHeaderDisplayed(header)) {
        header.display = 'y';
        visibleCount++;
      }
    }
  }

  private isGridInlineEditActive(): boolean {
    if (!this.ggGrid?.length) {
      return false;
    }
    const $grid: any = this.ggGrid;
    if (typeof $grid.find !== 'function') {
      return false;
    }
    return $grid.find('.edit-grid-value:visible').length > 0
      || $grid.find('.gg-input-text:focus').length > 0;
  }

  private scheduleGridRefresh(): void {
    // Defer to next frame so Angular change detection + CSS layout can assign
    // final dimensions before gizmoGrid recomputes column widths. Skip refresh
    // while inline edit is active and avoid redundant full rebuilds — they
    // reset selection/edit state and cause "first click doesn't edit" glitches.
    const token = ++this.gridLayoutRefreshToken;
    const raf = window.requestAnimationFrame || ((cb: any) => setTimeout(cb, 16));

    const needsLayoutRetry = (): boolean => {
      const host = this.element?.nativeElement as HTMLElement | undefined;
      if (!host) {
        return false;
      }
      return host.classList.contains('gt-grid-hidden') || host.clientWidth === 0;
    };

    const runRefresh = (allowLayoutRetry: boolean) => {
      if (token !== this.gridLayoutRefreshToken) {
        return;
      }
      if (this.isGridInlineEditActive()) {
        return;
      }
      if (this.ggApi?.gridRefresh) {
        this.ggApi.gridRefresh();
      }
      if (allowLayoutRetry && needsLayoutRetry()) {
        raf(() => runRefresh(false));
      }
    };

    raf(() => runRefresh(true));
  }

  private collectMeKeys(nodes: any): string[] {
    const allKeys: string[] = [];
    const arr = _.isArray(nodes) ? nodes : [];
    const collect = (items: any[]) => {
      for (const item of items) {
        const k = item?.key ?? item?.KEY ?? item?.VALUE ?? item?.ME_KEY
          ?? item?.parnet ?? item?.value;
        const str = k != null ? String(k) : '';
        if (str !== '' && !/^9+$/.test(str)) {
          allKeys.push(str);
        }
        if (item?.children?.length) {
          collect(item.children);
        }
      }
    };
    collect(arr);
    return [...new Set(allKeys)];
  }

  // ---------------------------------------------------------------------------
  // Callback handlers — link, form, modal
  // ---------------------------------------------------------------------------

  private handleLink(_data: any): void {
    if (_data.colData?.link) {
      const objCol = JSON.parse(_data.colData.link);
      const tableState = _data.colData.table_state
        ? JSON.parse(_data.colData.table_state)
        : null;
      this.workspaceService.loadLink(
        'link', _data, objCol.activity_key, objCol.screen_key,
        _data.rowData, tableState, null, null, false
      );
    }
  }

  private handleFormCallback(object: any): void {
    const callName = object.callName;
    const _data = object.data;

    // Pre-resolve a candidate CE tag from the registry so we know what to
    // render after the lock-check response comes back. This mirrors legacy
    // behaviour where $scope.openModal was always called only AFTER
    // ModalFactory.getModal() returned, regardless of whether the modal was
    // already "migrated" in the registry.
    const colCallBack = _data?.colData?.call_back;
    const registryEntry = this.componentRegistry.resolve(callName);
    const registryByCol = colCallBack ? this.componentRegistry.resolve(colCallBack) : undefined;

    const chosenEntry = registryByCol || registryEntry;
    const modalName = colCallBack || callName;

    if (!modalName) {
      // No modal name AND no registry hit ⇒ nothing we can do. With a
      // registry hit (batch actions dispatched by gizmoGrid that don't
      // surface a modal name), open it directly — matches legacy
      // handleOpenGridModal fallback.
      if (chosenEntry) {
        this.openCeDialog(chosenEntry.ceTagName, _data, chosenEntry, callName);
        return;
      }
      console.warn('[GridDirective] Unhandled form callback:', callName);
      return;
    }

    // Always call /getScreenModal first — legacy parity. The server response
    // carries the lock-check result (`lockResult.is_locked`), the modal_size
    // / modal_desc the dialog should adopt, and the canonical `ce_tag_name`
    // when the screen-modal config has been migrated server-side. Skipping
    // this call (the previous "fast-path") meant lock-guarded modals such as
    // `s3TemplateView` opened without consulting the backend.
    //
    // If the server does not recognise the modal name (typical for batch
    // actions whose modal is only defined client-side) we fall back to the
    // registry entry so the dialog still opens.
    this.modalFactory.getModal(modalName, _data).subscribe(
      (response: any) => {
        if (!response || response.callSuccess !== '1') {
          if (chosenEntry) {
            this.openCeDialog(chosenEntry.ceTagName, _data, chosenEntry, modalName);
            return;
          }
          this.showModalOpenError(response?.errorMessage);
          return;
        }

        // CE tag resolution order (mirrors handleOpenGridModal):
        //   1. response.modalScreen.ce_tag_name — server-driven, set
        //      directly in `Screen Modal` config when the modal has been
        //      migrated to a custom element (e.g. ghostSettelmentRedirection).
        //   2. registry by colCallBack / callName / response.modalScreen.modal_name
        //      — local fallback when the server does not yet expose ce_tag_name.
        const serverCeTag: string | undefined = response?.modalScreen?.ce_tag_name;
        const responseModalName: string | undefined = response?.modalScreen?.modal_name;
        const resolvedEntry =
          this.componentRegistry.resolve(colCallBack || '') ||
          this.componentRegistry.resolve(callName) ||
          (responseModalName ? this.componentRegistry.resolve(responseModalName) : undefined) ||
          (serverCeTag ? this.componentRegistry.resolve(serverCeTag) : undefined);
        const chosenTag = serverCeTag || resolvedEntry?.ceTagName;

        if (!chosenTag) {
          console.warn('[GridDirective] No CE tag resolved for modal:', modalName, response?.modalScreen);
          this.showModalOpenError();
          return;
        }

        const lockPayload = this.buildLockPayload(response);
        const dialogWidth = responseModalName === 's3TemplateView-sm' && resolvedEntry?.dialogWidth
          ? resolvedEntry.dialogWidth
          : this.resolveDialogWidth(
            response?.modalScreen?.modal_size,
            resolvedEntry?.dialogWidth,
          );
        const modalDesc = response?.modalScreen?.modal_desc;
        this.openCeDialog(chosenTag, _data, resolvedEntry, modalName, lockPayload, dialogWidth, modalDesc);
      },
      (err: any) => {
        console.error('[GridDirective] getScreenModal error:', modalName, err);
        // Network / 5xx fallback: still try the registry so the user can
        // proceed for modal names the server doesn't ack (batch actions).
        if (chosenEntry) {
          this.openCeDialog(chosenEntry.ceTagName, _data, chosenEntry, modalName);
          return;
        }
        this.showModalOpenError();
      }
    );
  }

  /**
   * Translate the legacy `/getScreenModal` lock contract into an Angular-friendly
   * payload. Mirrors the AngularJS condition:
   *   data.lockResult && data.lockResult.is_locked
   *     && data.modalScreen.lock_check_required == "Y"
   */
  private buildLockPayload(response: any): ModalLockPayload | undefined {
    const checkRequired = response?.modalScreen?.lock_check_required === 'Y';
    const isLocked = response?.lockResult?.is_locked === 'Y';
    if (!checkRequired || !isLocked) {
      return undefined;
    }
    return {
      isLocked: true,
      checkRequired: true,
      message: response?.lockResult?.lock_message || '',
    };
  }

  private showModalOpenError(message?: string): void {
    this.gtwSnackbarService.openSnackBar(
      message || 'Unable to open the modal. Please try again.',
      'gtw-snackbar--red'
    );
  }

  /**
   * Map the legacy `modalScreen.modal_size` string to a CSS width accepted by
   * MatDialog.open({ width }). Mirrors the UI Bootstrap sizes used by
   * `$uibModal.open({ size })` in `gridController.js` and the width overrides
   * defined in `ui/assets/less/bootstrap-override.less`.
   *
   * Precedence: backend modal_size wins; fallback is the registry-configured
   * dialogWidth (or '90vw' when absent).
   */
  private resolveDialogWidth(modalSize?: string, fallback?: string): string {
    const fb = fallback ?? '90vw';
    if (!modalSize) return fb;
    const s = String(modalSize).trim().toLowerCase();
    switch (s) {
      case 'sm': return '350px';
      case 'md': return '70vw';
      case 'lg': return '92vw';
      case 'xl': return '96vw';
    }
    if (/^\d+(\.\d+)?(px|%|vw|vh|em|rem)$/.test(s)) return s;
    if (/^\d+(\.\d+)?$/.test(s)) return `${s}px`;
    return fb;
  }

  private openCeDialog(
    ceTagName: string,
    _data: any,
    registryEntry?: any,
    callName?: string,
    lockPayload?: ModalLockPayload,
    widthOverride?: string,
    modalDesc?: string,
  ): void {
    const dialogData = this.prepareDialogData(
      ceTagName,
      _data,
      callName,
      lockPayload,
      modalDesc,
      registryEntry?.templateMode,
    );

    const basePanel = registryEntry?.dialogPanelClass ?? 'ce-host-dialog-panel';
    // MatDialog passes each entry to classList.add — one string must be one token
    // (no spaces). Registry may use space-separated classes in a single string.
    const rawPanels: string[] = Array.isArray(basePanel) ? [...basePanel] : [String(basePanel)];
    const panelClass: string[] = [];
    for (const token of rawPanels) {
      for (const part of String(token).trim().split(/\s+/)) {
        if (part) {
          panelClass.push(part);
        }
      }
    }
    if (lockPayload?.isLocked) {
      panelClass.push('modal-window-lock-class');
    }

    const isEfileCommentsModal =
      typeof ceTagName === 'string' && ceTagName.toLowerCase().includes('efile-comments-modal');
    if (isEfileCommentsModal) {
      panelClass.push('ce-host-dialog-efile-comments');
    }

    const dialogRef = this.dialog.open(CeHostDialogComponent, {
      data: dialogData,
      width: widthOverride ?? registryEntry?.dialogWidth ?? '90vw',
      height: isEfileCommentsModal ? undefined : registryEntry?.dialogHeight ?? undefined,
      panelClass,
      disableClose: true,
      ...(isEfileCommentsModal ? { position: { top: '48px' } } : {}),
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
      if (result?.openSource && result?.tagKey && activeScreen?.screen_key) {
        this.loadGridData(activeScreen, true);
        this.openSourcePageForNewTag(activeScreen, String(result.tagKey));
        return;
      }
      if (result?.action === 'refresh') {
        if (activeScreen?.screen_key) {
          this.loadGridData(activeScreen, true);
        }
      }
    });
  }

  private resolveGridApiGet(activeScreen: any): { fullUrl: string; legacyExtraParams?: Record<string, any> } | null {
    const routeConfig = this.appService.ROUTES?.[activeScreen?.link];
    let apiGet: string | undefined = routeConfig?.api?.get;
    let legacyExtraParams: Record<string, any> | undefined;

    if (!apiGet) {
      const legacyApi = LEGACY_ROUTE_API_GET[activeScreen?.link];
      if (legacyApi) {
        apiGet = legacyApi.url;
        legacyExtraParams = legacyApi.params;
      } else {
        return null;
      }
    }

    let fullUrl = apiGet;
    if (!fullUrl.startsWith('/') && !fullUrl.startsWith('http')) {
      fullUrl = environment.base_url + '/' + fullUrl;
    }
    return { fullUrl, legacyExtraParams };
  }

  private isPdfIssueScenarioGridLoad(activeScreen: any, fullUrl: string): boolean {
    const screenKey = String(activeScreen?.screen_key ?? '');
    if (PDF_ISSUE_SCENARIO_GRID_LOADS.some((config) => config.screenKey === screenKey)) {
      return true;
    }

    const url = String(fullUrl || '');
    return PDF_ISSUE_SCENARIO_GRID_LOADS.some(
      (config) =>
        this.hasQueryParamValue(url, 'grid_id', config.gridId) &&
        this.hasQueryParamValue(url, 'action_id', config.actionId)
    );
  }

  private hasQueryParamValue(url: string, paramName: string, expectedValue: string): boolean {
    const queryStart = url.indexOf('?');
    if (queryStart === -1) {
      return false;
    }

    return url
      .slice(queryStart + 1)
      .split('&')
      .some((part: string) => {
        const [key, value = ''] = part.split('=');
        return decodeURIComponent(key) === paramName && decodeURIComponent(value) === expectedValue;
      });
  }

  /**
   * Legacy AttributeCtrl.showSoucePage after Add Tag → Save and Source:
   * reload grid rows, find the new TAG_KEY, open gtw-ftc-sourcing-summary-ce.
   */
  private openSourcePageForNewTag(activeScreen: any, tagKey: string): void {
    const resolved = this.resolveGridApiGet(activeScreen);
    if (!resolved) {
      this.gtwSnackbarService.openSnackBar(
        'Unable to open Source page: grid API not configured.',
        'gtw-snackbar--red',
        true,
        5
      );
      return;
    }

    const filterParams = activeScreen.filters?.getFilterParams
      ? activeScreen.filters.getFilterParams()
      : {};
    const legacyRuntimeParams = activeScreen.legacyFilterParams;

    const params = _.merge(
      {},
      this.globalService.global?.globalParams || {},
      filterParams,
      resolved.legacyExtraParams || {},
      legacyRuntimeParams || {},
      { object_id: undefined }
    );

    delete params.scenario_v1;
    delete params.scenarioCode;
    delete params.is_issue_key_enabled;
    delete params.scenario_desc;

    if (params.tax_year && this.globalService.getScenario) {
      const resolvedScenario = this.globalService.getScenario(params.tax_year);
      if (resolvedScenario != null) {
        params.scenario = resolvedScenario;
      }
    }

    this.gridFactory.getDataUpdate(resolved.fullUrl, params).subscribe({
      next: (dataInfo: any) => {
        const rows = dataInfo?.data;
        if (!Array.isArray(rows) || rows.length === 0) {
          this.gtwSnackbarService.openSnackBar(
            'Unable to open Source page: saved tag row not found.',
            'gtw-snackbar--red',
            true,
            5
          );
          return;
        }

        const rowData =
          rows.length === 1
            ? rows[0]
            : rows.find((item: any) => String(item.TAG_KEY) === String(tagKey));

        if (!rowData) {
          this.gtwSnackbarService.openSnackBar(
            'Unable to open Source page: saved tag row not found.',
            'gtw-snackbar--red',
            true,
            5
          );
          return;
        }

        const colData = NEW_TAG_SOURCE_COL_DATA;
        const registryEntry = this.componentRegistry.resolve('newTagSummaryFTC');
        const modalData = { rowData, colData, gridData: { rowData, colData } };
        this.openCeDialog(
          'gtw-ftc-sourcing-summary-ce',
          modalData,
          registryEntry,
          'newTagSummaryFTC'
        );
      },
      error: () => {
        this.gtwSnackbarService.openSnackBar(
          'Unable to load tag data for Source page.',
          'gtw-snackbar--red',
          true,
          5
        );
      },
    });
  }

  private prepareDialogData(
    ceTagName: string,
    _data: any,
    callName?: string,
    lockPayload?: ModalLockPayload,
    modalDesc?: string,
    templateMode?: string,
  ): CeHostDialogData {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    const filterParams = activeScreen?.filters?.getFilterParams
      ? activeScreen.filters.getFilterParams()
      : {};

    const globalParams = { ...(this.globalService.global?.globalParams || {}) };
    globalParams.screen_key = activeScreen?.screen_key || globalParams.screen_key;

    const headerData = {
      globalParams,
      userSettingsData: this.appService.USER_SETTINGS || {},
      serverConfigData: this.appService.SERVER_CONFIG || {},
      generalConfigData: { ...environment },
    };

    const baseUrls = {
      api: environment.base_url,
      admin: environment.admin_api_url,
      custom: environment.custom_api_url,
      efile: environment.efile_api_url,
      pdfEngine: environment.pdf_engine_api_url,
      bulkPdfEngine: environment.bulk_pdf_engine_api_url,
    };

    // Legacy AngularJS modal templates (e.g. `efile-edit-submission-table-data.html`)
    // pass the grid's column metadata to the hosted custom element via
    // `header-label="{{ctrl.headers | json}}"`, where `ctrl.headers` is read
    // from `workspaceFactory.activeScreen.data.header`. We mirror that contract
    // here so modal-form components can still build their field list from the
    // originating grid's headers without being rewritten.
    const headerLabel = activeScreen?.data?.header ?? null;

    // gizmoGrid dispatches form callbacks with two different shapes:
    //  - Pencil / inline-action button: `_data = { colData, rowData }`
    //  - Toolbar "Actions" / batch action menu (base.api.batchAction): `_data` is
    //    the `base.batchSelect` array of `{ row_id, data: <row> }` entries.
    // Without unwrapping the array, modal-form components see an empty rowData
    // and render blank inputs. Mirror gizmoGrid's batchSelect entry shape and
    // fall back gracefully so single-row modals keep working from both paths.
    const isBatchArray = Array.isArray(_data);
    const firstBatchEntry = isBatchArray ? _data[0] : null;
    const rowDataResolved =
      _data?.rowData
      ?? firstBatchEntry?.data
      ?? firstBatchEntry
      ?? {};
    const colDataResolved =
      _data?.colData
      ?? firstBatchEntry?.colData
      ?? {};

    const modalCall = callName || colDataResolved?.call_back || '';
    let extraAttributes: Record<string, any> | undefined;
    if (modalCall === 'gridActionModal' || modalCall === 'gridPivotActionModal') {
      const actionTemplates = activeScreen?.data?.actionTemplates;
      if (Array.isArray(actionTemplates) && actionTemplates.length > 0) {
        extraAttributes = { 'action-templates': actionTemplates };
      }
    }

    return {
      ceTagName,
      rowData: rowDataResolved,
      colData: colDataResolved,
      headerData,
      baseUrls,
      filterParams,
      gridData: _data,
      modalName: modalCall,
      modalDesc,
      lock: lockPayload,
      headerLabel,
      templateMode,
      extraAttributes,
    };
  }

  private openModal(_data: any): void {
    if (_data.colData?.modal_name) {
      this.modalFactory.getModal(_data.colData.modal_name, _data).subscribe(
        (response: any) => {
          if (response) {
            this.dialog.open(response.component || _data.component, {
              data: { ...response, rowData: _data.rowData },
            });
          }
        }
      );
    }
  }

  // ---------------------------------------------------------------------------
  // Save handlers — input, inline-save, batch-save, columnTemplate
  // ---------------------------------------------------------------------------

  private inlineSave(_data: any): void {
    if (_data.url) {
      this.gridSaveFactory.saveGridData(_data.url, _data.params).subscribe(
        (response: any) => {
          if (response.callSuccess == '1') {
            this.gridEventService.broadcast('grid:refresh', {
              screenKey:
                this.workspaceService.workspaceFactory.activeScreen.screen_key,
            });
          }
        }
      );
    }
  }

  private handleInputSave(callName: string, _data: any): void {
    if (!callName) return;
    const globalParams = this.globalService.global?.globalParams || {};
    this.gridSaveFactory.saveSwitch(callName, _data, globalParams);

    if (_data?.colData?.audit_cells === '1') {
      const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
      const filterCache = activeScreen?.filters?.getFilterParamsFromCache
        ? activeScreen.filters.getFilterParamsFromCache()
        : {};
      this.gridSaveFactory.saveAuditCellData(_data, globalParams, filterCache);
    }
  }

  private handleBatchSave(_callName: string, _data: any): void {
    if (_data?.colData && !_data.colData.removable) {
      this.gridEventService.broadcast('dataChanged', _data);
    }
  }

  private handleColumnTemplate(callName: string, _data: any): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    const gridKey = activeScreen?.data?.settings?.grid_key;
    if (!gridKey) return;

    const filterParams = activeScreen.filters?.getFilterParams
      ? activeScreen.filters.getFilterParams()
      : {};
    const globalParams = this.globalService.global?.globalParams || {};

    this.gridSaveFactory.colTemplatesUpdate(gridKey, callName, _data, filterParams, globalParams);
  }

  // ---------------------------------------------------------------------------
  // Modal-based handlers — openGridModal, comment, attachment, preview, auditcell
  // ---------------------------------------------------------------------------

  private handleOpenGridModal(callName: string, _data: any): void {
    // Legacy parity: always call /getScreenModal first so the backend
    // lock-check (`lockResult.is_locked`) and server-driven modal sizing
    // metadata run before the dialog opens. The previous fast-path
    // (registry-hit → openCeDialog immediately) skipped this call, so
    // lock-guarded modals such as `s3TemplateView` could open while the
    // record was locked by another user.
    //
    // The registry is still consulted as a fallback when the server does
    // not recognise the modal name (typical for batch / client-only modals).
    const registryEntry = this.componentRegistry.resolve(callName);

    this.modalFactory.getModal(callName, _data).subscribe(
      (response: any) => {
        if (response?.modalScreen) {
          const ceTag = response.modalScreen.ce_tag_name;
          const lockPayload = this.buildLockPayload(response);
          const dialogWidth = this.resolveDialogWidth(
            response.modalScreen.modal_size,
            registryEntry?.dialogWidth,
          );
          const modalDesc = response.modalScreen.modal_desc;
          if (ceTag) {
            this.openCeDialog(ceTag, _data, registryEntry, callName, lockPayload, dialogWidth, modalDesc);
          } else if (registryEntry) {
            this.openCeDialog(
              registryEntry.ceTagName, _data, registryEntry, callName, lockPayload, dialogWidth, modalDesc,
            );
          } else {
            this.showModalOpenError();
          }
          return;
        }
        // Server didn't return a modalScreen entry — fall back to the
        // registry so client-only modals still work.
        if (registryEntry) {
          this.openCeDialog(registryEntry.ceTagName, _data, registryEntry, callName);
        } else {
          this.showModalOpenError();
        }
      },
      (err: any) => {
        console.error('[GridDirective] openGridModal error:', callName, err);
        if (registryEntry) {
          this.openCeDialog(registryEntry.ceTagName, _data, registryEntry, callName);
        } else {
          this.showModalOpenError();
        }
      }
    );
  }

  private handleModalByFactory(callName: string, _data: any): void {
    const registryEntry = this.componentRegistry.resolve(callName);
    if (registryEntry) {
      this.openCeDialog(registryEntry.ceTagName, _data, registryEntry, callName);
      return;
    }

    this.modalFactory.getModal(callName, _data).subscribe(
      (response: any) => {
        if (response?.modalScreen) {
          const ceTag = response.modalScreen.ce_tag_name;
          if (ceTag) {
            this.openCeDialog(ceTag, _data);
          } else {
            const registryByModalName = this.componentRegistry.resolve(callName);
            if (registryByModalName) {
              this.openCeDialog(registryByModalName.ceTagName, _data, registryByModalName);
            } else {
              this.dialog.open(response.component || _data.component, {
                data: { ...response, rowData: _data.rowData, gridData: _data },
                panelClass: 'ce-host-dialog-panel',
                disableClose: true,
              });
            }
          }
        }
      },
      (err: any) => console.error('[GridDirective] modalByFactory error:', callName, err)
    );
  }

  // ---------------------------------------------------------------------------
  // Batch handlers — batchSave, row-added, removeBatchSelect
  // ---------------------------------------------------------------------------

  private handleBatchSaveAction(callName: string, _data: any): void {
    if (callName === 'batchSaveDelete') {
      this.gridEventService.broadcast('batchSaveDelete', _data);
    } else if (callName === 'batchSaveUnDelete') {
      this.gridEventService.broadcast('batchSaveUnDelete', _data);
    }
  }

  private handleRowAdded(_callName: string, _data: any): void {
    this.gridEventService.broadcast('rowAdded', _data);
  }

  private handleRemoveBatchSelect(_data: any): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    if (activeScreen?.data) {
      activeScreen.data.batchSelect = _data.batchSelect;
    }
  }

  /**
   * Batch "Remove Document" action — shows confirmation then POSTs to /removeAllBatchDocuments.
   * Ported from AngularJS gridController.js attachmentRemove controller.
   */
  private handleRemoveAttachment(_data: any): void {
    const dialogRef = this.dialog.open(SimpleConfirmDialogComponent, {
      data: {
        title: 'Confirm',
        message: 'Are you sure you want to delete the documents for all selected batch?',
        iconClass: 'fa fa-sign-out txt-color-orangeDark',
      },
      width: '420px',
      panelClass: 'simple-confirm-dialog-panel',
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.executeBatchDocumentRemoval(_data);
    });
  }

  private executeBatchDocumentRemoval(_data: any): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    const entities = Array.isArray(_data) ? _data : [];

    const trans_json: any[] = [];
    for (const entity of entities) {
      if (entity === 'selected') continue;
      const rowData = entity.data || entity;
      const docHeaderKey = activeScreen?.data?.settings?.doc_header_key;
      trans_json.push({
        parent_key: entity.row_id,
        parent_type: activeScreen?.label || '',
        doc_batch_id: rowData.DOC_BATCH_ID,
        combination_keys: rowData.COMBINATION_KEY,
        screen_name: activeScreen?.label || '',
        object_id: entity.row_id,
        combination_key: rowData.COMBINATION_KEY,
        header_key: docHeaderKey ? rowData[docHeaderKey] : '',
        doc_keys: rowData.DOC_KEYS,
      });
    }

    const filtered = trans_json.filter((item) => item.doc_batch_id != null);

    if (filtered.length === 0) {
      this.gtwSnackbarService.openSnackBar(
        'There is no documents on selected batch',
        'gtw-snackbar--yellow'
      );
      return;
    }

    const payload = {
      trans_json: JSON.stringify(filtered),
      grid_key: activeScreen?.data?.settings?.grid_key || '',
    };

    this.http
      .post<any>(environment.base_url + '/removeAllBatchDocuments', payload, {
        withCredentials: true,
      })
      .pipe(take(1))
      .subscribe(
        (response: any) => {
          if (response?.callSuccess == 1 || response?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar(
              'Documents are deleted successfully',
              'gtw-snackbar--green'
            );
            if (activeScreen?.screen_key) {
              this.loadGridData(activeScreen, true);
            }
          } else {
            this.gtwSnackbarService.openSnackBar(
              response?.errorMessage || 'Error occurred while deleting documents',
              'gtw-snackbar--red'
            );
          }
        },
        (err: any) => {
          console.error('[GridDirective] removeAllBatchDocuments error:', err);
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while deleting documents. Please try again.',
            'gtw-snackbar--red'
          );
        }
      );
  }

  // ---------------------------------------------------------------------------
  // DrillDown handler
  // ---------------------------------------------------------------------------

  private handleDrillDown(_callName: string, _data: any): void {
    const executeDrillDown = (drillDownSource: string) => {
      try {
        const dataObj = JSON.parse(drillDownSource);
        const gridFilters: any[] = [];
        const linkFilters: any[] = [];

        if (dataObj.filters?.length) {
          _.forEach(dataObj.filters, (filterKey: string) => {
            let value: any;
            if (_data.rowData?.FROMDOCUMENT === 'document') {
              value = Number(_data.rowData.HEADER_KEY);
            } else {
              value = _data.rowData?.[filterKey];
            }
            if (value !== null && value !== undefined && (String(value).length || _.isNumber(value))) {
              gridFilters.push({
                predicate: filterKey, value: [value],
                type: 'array', reverse: false, operator: '[]',
              });
              // Keep legacy drilldown contract: pass source-row values as target filter name/value pairs.
              linkFilters.push({
                name: filterKey,
                value,
              });
            }
          });
        }

        if (dataObj.screenFilter?.length || dataObj.screen_filters?.length) {
          const screenFilters = dataObj.screenFilter || dataObj.screen_filters;
          _.forEach(screenFilters, (sf: any) => {
            const hasExplicitValue = Object.prototype.hasOwnProperty.call(sf || {}, 'value');
            const explicitValue = sf?.value;
            const key = sf.row_data_key || sf.name;
            const val = hasExplicitValue && explicitValue !== 'undefined'
              ? explicitValue
              : _data.rowData?.[key];

            if (val !== null && val !== undefined && (String(val).length || _.isNumber(val))) {
              // Screen-level filters go ONLY to linkFilters (for
              // applyCommentLinkFilters → screen filter dropdowns), NOT to
              // gridFilters (colSearch). Legacy AngularJS only put
              // dataObj.filters (row-level column values) into colSearch
              // chips. Pushing screen params (tax_year, scenario, etc.)
              // into colSearch causes gizmoGrid to filter by non-existent
              // columns, resulting in "Filtered: 0".
              linkFilters.push({
                filter_key: sf.filter_key,
                name: sf.name || key,
                value: val,
              });
            }
          });
        }

        const activityKey = dataObj.activity_key || _data.colData?.drilldown_activity_key;
        const screenKey = dataObj.screen_key || _data.colData?.drilldown_screen_key;
        const actionTemplate = dataObj.action_template_key || null;

        // Legacy parity (gridController.js:675-681): capture
        // `drilldown_filter_refresh` from the source column. When set to 1,
        // the target grid must build its POST body from row-level predicates
        // (e.g. combination_key) instead of the usual global/filter params.
        // Some backend rows ship the flag on dataObj itself rather than
        // colData — honor both. Falsy/missing → no injection (legacy parity).
        const drillDownRefresh =
          Number(_data.colData?.drilldown_filter_refresh ?? dataObj.drilldown_filter_refresh ?? 0) === 1 ? 1 : 0;

        console.log('[GridDirective] handleDrillDown: gridFilters (colSearch):', gridFilters.map((f: any) => f.predicate), 'linkFilters:', linkFilters.length, 'drillDownRefresh:', drillDownRefresh);
        if (activityKey && screenKey) {
          this.workspaceService.loadLink(
            'drillDown', _data, activityKey, screenKey,
            linkFilters.length ? linkFilters : (dataObj.screenFilter || dataObj.filter_json || gridFilters),
            gridFilters, null, actionTemplate, false, drillDownRefresh
          );
        } else if (screenKey) {
          const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
          this.workspaceService.loadLink(
            'drillDown', _data, activeScreen?.activity_key, screenKey,
            linkFilters.length ? linkFilters : (dataObj.screenFilter || dataObj.filter_json || gridFilters),
            gridFilters, null, actionTemplate, false, drillDownRefresh
          );
        }
      } catch (e) {
        console.error('[GridDirective] drillDown parse error:', e);
      }
    };

    if (_data.rowData?.DRILLDOWN) {
      executeDrillDown(_data.rowData.DRILLDOWN);
    } else if (_data.colData?.drilldown) {
      try {
        const parsed = JSON.parse(_data.colData.drilldown);
        if (_.isArray(parsed)) {
          let found = false;
          _.each(parsed, (item: any) => {
            if (item.rule != null && !found) {
              const rule = item.rule;
              let isValid = false;
              if (rule.operator === '=' && rule.value == _data.rowData?.[rule.map]) {
                isValid = true;
              } else if (rule.operator === '!=' && rule.value != _data.rowData?.[rule.map]) {
                isValid = true;
              }
              if (isValid) {
                found = true;
                executeDrillDown(JSON.stringify(item.drillDown));
              }
            }
          });
        } else {
          executeDrillDown(_data.colData.drilldown);
        }
      } catch (_e) {
        executeDrillDown(_data.colData.drilldown);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Remaining handlers — reloadComment, reloadAttachment, downloadDoc, applyPivotTemplate
  // ---------------------------------------------------------------------------

  private handleReloadComment(_data: any): void {
    const type = _data?.type || 'all';
    this.gridEventService.broadcast('loadGridMessages', type, true);
  }

  private handleReloadAttachment(): void {
    this.gridEventService.broadcast('loadGridAttachments', true);
  }

  private handleDownloadDoc(_callName: string, _data: any): void {
    if (_callName === 'downloadDocuments') {
      // AngularJS returns true to signal the download was handled by gizmoGrid internally
    }
  }

  private handleApplyPivotTemplate(_callName: string, _data: any): void {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    if (!activeScreen?.data || !_data) return;

    if (!_.isEmpty(_data.pivotGroupObject)) {
      activeScreen.data.pivotRowList = [];
      activeScreen.data.pivotColumn = [];

      if (_data.pivotGroupObject.pivotRowList) {
        activeScreen.data.pivotRowList = _data.pivotGroupObject.pivotRowList;
      }
      if (_data.pivotGroupObject.pivotColumn) {
        activeScreen.data.pivotColumn = _data.pivotGroupObject.pivotColumn;
      }

      activeScreen.data.pivotGroup = _data.pivotGroupObject;
      this.gridEventService.broadcast('applyPivotPanel', _data.pivotGroupObject);
    }

    if (_data.clearActionTemplate) {
      if (activeScreen.data.pivotGroup) {
        activeScreen.data.pivotGroup = {};
      }
      this.gridEventService.broadcast('dataLoaded', activeScreen.data);
    }
  }

  // ---------------------------------------------------------------------------
  // Export to Excel helpers
  // ---------------------------------------------------------------------------

  /** Legacy gridController.isFiltered() — colSearch or selected grid action. */
  private isFilteredExportAvailable(): boolean {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen;
    const colSearch = this.resolveExportColSearch(activeScreen);
    const selectedAction = _.filter(activeScreen?.data?.actionTemplates || [], { selectedAction: true });
    return colSearch.length > 0 || selectedAction.length > 0;
  }

  /**
   * Resolve live column filters for export. gizmoGrid owns the authoritative
   * tableState while the grid is interactive; applying a grid action replaces
   * options.data.tableState without updating activeScreen.data.tableState.
   */
  private resolveExportColSearch(activeScreen: any): any[] {
    const liveColSearch = this.ggApi?.getTableState?.()?.colSearch;
    if (Array.isArray(liveColSearch) && liveColSearch.length > 0) {
      return liveColSearch;
    }

    const selectedActions = _.filter(activeScreen?.data?.actionTemplates || [], { selectedAction: true });
    for (const action of selectedActions) {
      const rawGridData = action?.grid_data ?? action?.gridData;
      if (!rawGridData) {
        continue;
      }
      try {
        const parsed = typeof rawGridData === 'string' ? JSON.parse(rawGridData) : rawGridData;
        const savedColSearch = parsed?.tableState?.colSearch;
        if (Array.isArray(savedColSearch) && savedColSearch.length > 0) {
          return savedColSearch;
        }
      } catch {
        // Ignore malformed saved grid action payloads.
      }
    }

    const screenColSearch = activeScreen?.data?.tableState?.colSearch;
    return Array.isArray(screenColSearch) ? screenColSearch : [];
  }

  private syncFilteredExportVisibility(): void {
    if (!this.ggGrid?.length) {
      return;
    }
    const show = this.isFilteredExportAvailable();
    this.ggGrid
      .find('.gg-export-action[action="filtered-export"]')
      .css('display', show ? '' : 'none');
  }

  private showExportSpinner(): void {
    if (!this.ggGrid) return;
    const toolsExport = this.ggGrid.find('.tools-export');
    toolsExport.addClass('gg-export-to-excel');
    toolsExport.append('<i class="fa fa-spinner fa-spin"></i>');
  }

  private hideExportSpinner(): void {
    if (!this.ggGrid) return;
    const toolsExport = this.ggGrid.find('.tools-export');
    toolsExport.removeClass('gg-export-to-excel');
    toolsExport.find('i.fa-spinner').remove();
  }

  /**
   * Import-screen export files include a summary section derived from jsonObj.
   * These keys should not appear in that exported summary block.
   */
  private stripNonRequiredImportExportSummaryKeys(jsonData: any): any {
    const sanitized: any = _.cloneDeep(jsonData || {});
    const keysToRemove = [
      'tax_year',
      'scenario_v1',
      'scenarioCode',
      'is_issue_key_enabled',
      'scenario_desc',
      'p_form',
      'p_schedule',
      'p_filer_key',
      'p_filer_partner_key',
      'isFileDownloadRequest',
      'display_tax_year',
      'display_scenario',
      'display_scenario_v1',
      'display_scenarioCode',
      'display_is_issue_key_enabled',
      'display_scenario_desc',
      'display_scenario_description',
      'display_p_form',
      'display_p_schedule',
      'display_p_filer_key',
      'display_p_filer_partner_key'
    ];
    keysToRemove.forEach((key: string) => delete sanitized[key]);
    // Legacy import exports should not print P* metadata rows in file header.
    Object.keys(sanitized).forEach((key: string) => {
      const normalizedKey = key.toLowerCase();
      if (normalizedKey.startsWith('p_') || normalizedKey.startsWith('display_p_')) {
        delete sanitized[key];
      }
    });
    return sanitized;
  }

  private buildExportGridSettings(settings: any): string {
    // Legacy gizmoGrid appends `base.data.settings` directly to FormData.
    // For import grids, `settings` is often undefined; when migrated code
    // stringified an empty object, the request sent `gridSettings={}` and some
    // backend branches treated it as a provided-but-empty settings payload.
    // Preserve legacy semantics: if settings is missing or empty, send the
    // literal undefined marker instead of `{}`.
    if (settings == null || (_.isObject(settings) && _.isEmpty(settings))) {
      return 'undefined';
    }
    return _.isString(settings) ? settings : JSON.stringify(settings);
  }

  private buildExportParams(): {
    jsonData: any; actionId: string; actionCode: string; gridId: string;
    ssoId: string; screenKey: string; activeScreen: any;
  } | null {
    const activeScreen = this.workspaceService.workspaceFactory.activeScreen;
    if (!activeScreen?.screen_key) return null;

    const routeConfig = this.resolveRouteConfig(activeScreen);
    const routeApi = routeConfig?.api || {};
    const apiGet: string = routeApi?.get || '';
    const apiSave: string = routeApi?.save || '';

    let actionId = '';
    let actionCode = '';
    let gridId = '';
    const parseApiQuery = (apiUrl: string) => {
      const out: { actionId: string; actionCode: string; gridId: string } = {
        actionId: '',
        actionCode: '',
        gridId: '',
      };
      const qsIndex = String(apiUrl || '').indexOf('?');
      if (qsIndex === -1) return out;
      const qs = String(apiUrl).substring(qsIndex + 1);
      qs.split('&').forEach((pair: string) => {
        const [rawKey, rawVal] = pair.split('=');
        const key = String(rawKey || '').trim();
        const val = decodeURIComponent(String(rawVal || '').trim());
        if (!val) return;
        if (key === 'action_code') out.actionCode = val;
        else if (key.includes('action')) out.actionId = val;
        if (key.includes('grid')) out.gridId = val;
      });
      return out;
    };

    const pickFirstNonEmpty = (...vals: any[]): string => {
      for (const val of vals) {
        if (val === undefined || val === null) continue;
        const text = String(val).trim();
        if (text !== '' && text !== 'undefined' && text !== 'null') {
          return text;
        }
      }
      return '';
    };

    const getMeta = parseApiQuery(apiGet);
    const saveMeta = parseApiQuery(apiSave);
    actionId = pickFirstNonEmpty(getMeta.actionId, saveMeta.actionId);
    actionCode = pickFirstNonEmpty(getMeta.actionCode, saveMeta.actionCode);
    gridId = pickFirstNonEmpty(getMeta.gridId, saveMeta.gridId);

    // Fallbacks for routes where query params are not present in api.get
    // but grid metadata is available after loadGridJson.
    const settings = activeScreen?.data?.settings || {};
    if (!actionId) {
      actionId = pickFirstNonEmpty(
        routeApi.action_id,
        routeApi.actionId,
        settings.action_id,
        settings.action_key,
        settings.actionId,
        settings.batch_save_action_key,
        activeScreen?.batch_save_action_key,
      );
    }
    if (!actionCode) {
      actionCode = pickFirstNonEmpty(
        routeApi.action_code,
        routeApi.actionCode,
        settings.action_code,
        settings.actionCode,
        activeScreen?.action_code,
      );
    }
    if (!gridId) {
      gridId = pickFirstNonEmpty(
        routeApi.grid_id,
        routeApi.gridId,
        settings.grid_id,
        settings.grid_key,
        settings.gridId,
      );
    }

    const filterParams = activeScreen.filters?.getFilterParams
      ? activeScreen.filters.getFilterParams()
      : {};

    const globalParams = this.globalService.global?.globalParams || {};
    const allParams: any = _.merge({}, globalParams, filterParams);

    const jsonData: any = { isFileDownloadRequest: 'Y' };
    Object.keys(allParams).forEach((k: string) => {
      const v = allParams[k];
      if (v != null && v !== '' && v !== 'undefined' && v !== 'null') {
        jsonData[k] = String(v);
      }
    });

    jsonData['display_tax_year'] = jsonData['tax_year'] || '';
    if (jsonData['system']) {
      jsonData['display_system'] = jsonData['system'];
    }
    if (jsonData['filter_by']) {
      jsonData['display_filter_by'] = jsonData['filter_by'];
    }

    if (activeScreen.filters?.filters) {
      const selectedFilters = activeScreen.filters.filters;

      const scenarioFilter = _.filter(selectedFilters, (f: any) => f.param_name === 'scenario');
      const filingGrpFilter = _.filter(selectedFilters, (f: any) => f.param_name === 'filing_key');

      const displayScenario = scenarioFilter.length > 0 ? scenarioFilter[0].selected?.LABEL : null;
      const displayFilingGrp = filingGrpFilter.length > 0 ? filingGrpFilter[0].selected?.LABEL : null;

      if (displayScenario) jsonData['display_scenario_description'] = displayScenario;
      if (displayFilingGrp) jsonData['display_filing_group_description'] = displayFilingGrp;

      const meFilter = _.filter(selectedFilters, (f: any) => f.param_name === 'parent_me_string');
      const selectedMeCodes: string[] = [];
      const iterChildren = (items: any[]) => {
        _.each(items, (item: any) => {
          if (item.selected) {
            selectedMeCodes.push(item.mekey || item.key || '');
          }
          if (item.children) iterChildren(item.children);
        });
      };
      if (meFilter.length) {
        _.each(meFilter, (f: any) => {
          if (f.selected?.length > 0) iterChildren(f.values || []);
        });
      }
      if (selectedMeCodes.length) {
        jsonData['display_mE_code'] = selectedMeCodes.join(',');
      }
    }

    const keysToExcludeDisplay = [
      'tax_year', 'screen_key', 'jcd_key', 'jcd_ta_key',
      'activity_key', 'screen_type', 'is_locked', 'isFileDownloadRequest'
    ];
    Object.keys(jsonData).forEach((k: string) => {
      if (!keysToExcludeDisplay.includes(k) && !k.startsWith('display_')) {
        jsonData['display_' + k] = jsonData[k];
      }
    });

    const ssoId = this.appService.USER_SETTINGS?.user?.sso_id || '';
    const screenKey = activeScreen.screen_key;

    return { jsonData, actionId, actionCode, gridId, ssoId, screenKey, activeScreen };
  }

  /**
   * Resolve route configuration even when server route keys are namespaced
   * as `workspace.<link>` but activeScreen.link is unprefixed.
   */
  private resolveRouteConfig(activeScreen: any): any {
    const link = activeScreen?.link;
    if (!link) return null;

    let routeConfig = this.appService.ROUTES?.[link];
    if (!routeConfig && !link.startsWith('workspace.')) {
      const prefixed = `workspace.${link}`;
      routeConfig = this.appService.ROUTES?.[prefixed];
    }
    return routeConfig;
  }

  private handleExportResponse(data: any, ssoId: string): void {
    const baseUrl = environment.base_url;
    if (data?.ERROR) {
      this.gtwSnackbarService.openSnackBar(data.ERROR, 'gtw-snackbar--red');
      this.exportDone();
      return;
    }
    if (data?.callSuccess != 1 && data?.callSuccess !== '1') {
      this.gtwSnackbarService.openSnackBar(
        'Data can not be exported, please contact System administrator',
        'gtw-snackbar--red'
      );
      this.exportDone();
      return;
    }

    this.http.get<any>(`${baseUrl}/getAPIAuthToken`).subscribe(
      (tokenRes: any) => {
        if (tokenRes?.apiToken) {
          const clientKey = sessionStorage.getItem('client_key') || '';
          const downloadUrl = `${baseUrl}/download?file_name=${encodeURIComponent(data.file_name)}`
            + `&client_key=${encodeURIComponent(clientKey)}`
            + `&apiToken=${encodeURIComponent(tokenRes.apiToken)}`
            + `&georaclehrid=${encodeURIComponent(ssoId)}`;
          window.open(downloadUrl, '_blank');
        } else {
          this.gtwSnackbarService.openSnackBar(
            'Failed to retrieve auth token for download',
            'gtw-snackbar--red'
          );
        }
        this.exportDone();
      },
      (_err: any) => {
        this.gtwSnackbarService.openSnackBar(
          'Error retrieving auth token. Please try again.',
          'gtw-snackbar--red'
        );
        this.exportDone();
      }
    );
  }

  private exportDone(): void {
    this.hideExportSpinner();
    this.workspaceService.downloadExcelDone.next();
  }

  // ---------------------------------------------------------------------------
  // Export to Excel — Download All Data
  // ---------------------------------------------------------------------------

  private exportToExcel(): void {
    const params = this.buildExportParams();
    if (!params) {
      this.workspaceService.downloadExcelDone.next();
      return;
    }

    this.showExportSpinner();

    const { jsonData, actionId, actionCode, gridId, ssoId, screenKey, activeScreen } = params;
    const exportScreenType = String(
      jsonData.screen_type ?? activeScreen?.screen_type ?? ''
    ).trim().toLowerCase();
    let isImportScreen =
      GridDirective.IMPORT_GRID_SCREEN_TYPES.includes(exportScreenType)
      || String(activeScreen?.data?.settings?.grid_type || '').trim().toLowerCase() === 'import';
    // Legacy gizmo-grid hard-coded dual-screen override.
    const exportScreenKey = String(jsonData.screen_key ?? screenKey ?? '');
    if (exportScreenKey === '11169' || exportScreenKey === '11471') {
      isImportScreen = true;
    }

    // Legacy parity for import screens: export endpoint can be invoked with an
    // empty action_id as long as grid_id is present. Non-import screens still
    // require action metadata.
    if (!gridId || (!isImportScreen && !actionId && !actionCode)) {
      this.gtwSnackbarService.openSnackBar(
        'Export is unavailable: grid metadata is missing (action/grid id).',
        'gtw-snackbar--red'
      );
      this.exportDone();
      return;
    }
    const baseUrl = environment.base_url;
    const actionParam = actionId
      ? `action_id=${encodeURIComponent(actionId)}`
      : (actionCode && !isImportScreen)
        ? `action_code=${encodeURIComponent(actionCode)}`
        : 'action_id=';
    let exportUrl = `${baseUrl}/exportToExcel?${actionParam}&grid_id=${encodeURIComponent(gridId)}&sso_id=${encodeURIComponent(ssoId)}&screen_key=${encodeURIComponent(screenKey)}`;
    const exportData = this.ggApi?.getExportData?.();
    const headerArray: any[] = exportData?.headerArray || activeScreen?.data?.header || [];
    const normalizedHeaders = headerArray.map((item: any) => ({
      object_id: item.object_id,
      label: item.label ?? null,
      data_type: item.data_type ?? null,
      format: item.format ?? null,
      map: item.map,
      width: item.width
    }));
    const exportJsonData = isImportScreen
      ? this.stripNonRequiredImportExportSummaryKeys(jsonData)
      : jsonData;
    const jsonObj = JSON.stringify(exportJsonData);
    const gridSettings: string = this.buildExportGridSettings(exportData?.settings);

    // Legacy parity:
    // - non-import "export all": urlencoded body (jsonObj/headerArray/gridSettings)
    // - import "export all":     compressed upload with file + query flags
    if (isImportScreen) {
      exportUrl += '&is_compressed=true&filtered_export=true';
      const filteredMap = normalizedHeaders.map((h: any) => h.map);
      const rowsSource: any[] =
        exportData?.safeCopyArr ||
        activeScreen?.data?.safeCopyArr ||
        exportData?.dataArr ||
        activeScreen?.data?.data ||
        [];
      const gridObject: any = { header: [filteredMap], data: [] as any[] };
      rowsSource.forEach((obj: any) => {
        const row: any[] = [];
        filteredMap.forEach((map: string) => {
          row.push(obj?.[map] === undefined ? null : obj[map]);
        });
        gridObject.data.push(row);
      });

      const compressedData = JSON.stringify(gridObject);
      const blob = new Blob([compressedData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const fd = new FormData();
      fd.append('file', blob);
      fd.append('jsonObj', jsonObj);
      fd.append('headerArray', JSON.stringify(normalizedHeaders));
      fd.append('gridSettings', gridSettings);

      this.http.post<any>(exportUrl, fd, { withCredentials: true }).subscribe(
        (data: any) => this.handleExportResponse(data, ssoId),
        (_err: any) => {
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while exporting. Please try again.',
            'gtw-snackbar--red'
          );
          this.exportDone();
        }
      );
      return;
    }

    const body =
      `jsonObj=${encodeURIComponent(jsonObj)}` +
      `&headerArray=${encodeURIComponent(JSON.stringify(normalizedHeaders))}` +
      `&gridSettings=${encodeURIComponent(gridSettings)}`;

    this.http.post<any>(exportUrl, body, {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8' }),
      withCredentials: true
    }).subscribe(
      (data: any) => this.handleExportResponse(data, ssoId),
      (_err: any) => {
        this.gtwSnackbarService.openSnackBar(
          'Error occurred while exporting. Please try again.',
          'gtw-snackbar--red'
        );
        this.exportDone();
      }
    );
  }

  // ---------------------------------------------------------------------------
  // Export to Excel — Download Filtered Data
  // ---------------------------------------------------------------------------

  private exportToExcelFiltered(): void {
    if (!this.isFilteredExportAvailable()) {
      this.workspaceService.downloadExcelDone.next();
      return;
    }

    const params = this.buildExportParams();
    if (!params) {
      this.workspaceService.downloadExcelDone.next();
      return;
    }

    this.showExportSpinner();

    const { jsonData, actionId, actionCode, gridId, ssoId, screenKey, activeScreen } = params;
    const exportScreenType = String(
      jsonData.screen_type ?? activeScreen?.screen_type ?? ''
    ).trim().toLowerCase();
    let isImportScreen =
      GridDirective.IMPORT_GRID_SCREEN_TYPES.includes(exportScreenType)
      || String(activeScreen?.data?.settings?.grid_type || '').trim().toLowerCase() === 'import';
    // Legacy gizmo-grid hard-coded dual-screen override.
    const exportScreenKey = String(jsonData.screen_key ?? screenKey ?? '');
    if (exportScreenKey === '11169' || exportScreenKey === '11471') {
      isImportScreen = true;
    }

    if (!gridId || (!isImportScreen && !actionId && !actionCode)) {
      this.gtwSnackbarService.openSnackBar(
        'Export is unavailable: grid metadata is missing (action/grid id).',
        'gtw-snackbar--red'
      );
      this.exportDone();
      return;
    }
    const baseUrl = environment.base_url;

    const colSearch = this.resolveExportColSearch(activeScreen);
    colSearch.forEach((col: any) => {
      if (col.predicate && col.value != null) {
        jsonData['colFilter_' + col.predicate] = col.value.toString();
      }
    });

    const liveTableState = this.ggApi?.getTableState?.();
    if (liveTableState && activeScreen.data) {
      activeScreen.data.tableState = liveTableState;
    }

    const exportData = this.ggApi?.getExportData?.();
    const headerArray: any[] = exportData?.headerArray || activeScreen.data?.header || [];
    const dataArr: any[] = exportData?.dataArr || [];
    const gridSettings: string = this.buildExportGridSettings(exportData?.settings);

    const headArra: any[] = [];
    const filteredMap: string[] = [];
    headerArray.forEach((item: any) => {
      headArra.push({
        object_id: item.object_id,
        label: item.label,
        data_type: item.data_type,
        format: item.format,
        map: item.map,
        width: item.width
      });
      filteredMap.push(item.map);
    });

    const exportJsonData = isImportScreen
      ? this.stripNonRequiredImportExportSummaryKeys(jsonData)
      : jsonData;
    const jsonObj = JSON.stringify(exportJsonData);
    const actionParam = actionId
      ? `action_id=${encodeURIComponent(actionId)}`
      : (actionCode && !isImportScreen)
        ? `action_code=${encodeURIComponent(actionCode)}`
        : 'action_id=';
    let exportUrl = `${baseUrl}/exportToExcel?${actionParam}&grid_id=${encodeURIComponent(gridId)}&sso_id=${encodeURIComponent(ssoId)}&screen_key=${encodeURIComponent(screenKey)}`;

    if (dataArr.length > 50000 && !isImportScreen) {
      exportUrl += '&is_compressed=false&filtered_export=false';
      const body = `jsonObj=${encodeURIComponent(jsonObj)}`
        + `&headerArray=${encodeURIComponent(JSON.stringify(headArra))}`
        + `&gridSettings=${encodeURIComponent(gridSettings)}`;

      this.http.post<any>(exportUrl, body, {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8' }),
        withCredentials: true
      }).subscribe(
        (data: any) => this.handleExportResponse(data, ssoId),
        (_err: any) => {
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while exporting. Please try again.',
            'gtw-snackbar--red'
          );
          this.exportDone();
        }
      );
    } else {
      exportUrl += '&is_compressed=true&filtered_export=true';

      const gridObject: any = { header: [filteredMap], data: [] as any[] };
      dataArr.forEach((obj: any) => {
        const row: any[] = [];
        filteredMap.forEach((map: string) => {
          row.push(obj[map] === undefined ? null : obj[map]);
        });
        gridObject.data.push(row);
      });

      const compressedData = JSON.stringify(gridObject);
      const blob = new Blob([compressedData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const fd = new FormData();
      fd.append('file', blob);
      fd.append('jsonObj', jsonObj);
      fd.append('headerArray', JSON.stringify(headArra));
      fd.append('gridSettings', gridSettings);

      this.http.post<any>(exportUrl, fd, { withCredentials: true }).subscribe(
        (data: any) => this.handleExportResponse(data, ssoId),
        (_err: any) => {
          this.gtwSnackbarService.openSnackBar(
            'Error occurred while exporting. Please try again.',
            'gtw-snackbar--red'
          );
          this.exportDone();
        }
      );
    }
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  ngOnDestroy(): void {
    this.destroyGrid();
    if (this.tabSub) {
      this.tabSub.unsubscribe();
    }
    if (this.refreshGridSub) {
      this.refreshGridSub.unsubscribe();
    }
    // `uiSub` is already cleaned up by `destroyGrid()` above. We deliberately
    // do NOT cancel any in-flight HTTP subscriptions here either — they live
    // in `TabGridLoaderService` so background fetches survive directive
    // teardown (cross-tab parity).
    if (this.refreshSub) {
      this.refreshSub.unsubscribe();
    }
    if (this.commentsSub) {
      this.commentsSub.unsubscribe();
    }
    if (this.downloadExcelSub) {
      this.downloadExcelSub.unsubscribe();
    }
  }
}
